import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  BookOpen, FileText, Settings, Plus, X, Trash2, GripVertical, Upload,
  LayoutGrid, FolderPlus, Layers, CheckSquare, ArrowRightLeft, Palette,
  Lock, Unlock,
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor,
  useSensor, useSensors, DragEndEvent, DragStartEvent, DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable,
  rectSortingStrategy, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RyanFieldHero from '../components/RyanFieldHero';
import RyanSubdeckCard from '../components/RyanSubdeckCard';
import RyanSubdeckGroupContainer from '../components/RyanSubdeckGroupContainer';
import RyanCreateGroupModal from '../components/RyanCreateGroupModal';
import RyanManageGroupsModal from '../components/RyanManageGroupsModal';
import RyanAssignGroupModal from '../components/RyanAssignGroupModal';
import RyanManageDisplayCategoriesModal from '../components/RyanManageDisplayCategoriesModal';
import RyanAssignCategoryModal from '../components/RyanAssignCategoryModal';
import RyanFlashcardPreviewModal from '../components/RyanFlashcardPreviewModal';
import RyanApostilaPreviewModal from '../components/RyanApostilaPreviewModal';
import InterdictedModal from '../../components/AccessControl/InterdictedModal';
import RyanMoveModal from '../components/RyanMoveModal';
import RyanCopyModal from '../components/RyanCopyModal';
import ImportModal from '../../components/deck_subdeck/ImportModal';
import ImportApostilaModal from '../../components/resumo/ImportApostilaModal';
import Toast from '../../components/Toast';
import { exportImportService } from '../../services/exportImport.service';
import { resumoService } from '../../services/resumo.service';
import { RyanTab, RyanConfig, RyanSubdeckGroupData, RyanDisplayCategoryData } from '../types/ryan.types';
import { useAuth } from '../../contexts/AuthContext';
import { useRyanAuth } from '../context/RyanAuthContext';
import { getRyanFieldColor, getRyanSubdeckColor, RYAN_SUBDECK_COLORS, getOrderedFieldColorFamilies } from '../constants/ryanFieldVisuals';
import { getRyanMedicalIcon } from '../constants/ryanMedicalIcons';
import RyanSubdeckIconPicker from '../components/RyanSubdeckIconPicker';
import RyanFieldProgressHero from '../components/RyanFieldProgressHero';
import RyanDomainRankingModal from '../components/RyanDomainRankingModal';
import RyanRestrictModal from '../components/RyanRestrictModal';
import './RyanFieldPage.css';

interface SubdeckData {
  _id: string;
  name: string;
  description?: string;
  type: 'flashcards' | 'apostila';
  hasResumo?: boolean;
  cardCount: number;
  deckId: string;
  tabId: string | null;
  groupId?: string | null;
  displayCategoryId?: string | null;
  masteryPercentage: number | null;
  lastReviewedAt?: string | null;
  colorId?: string;
  iconId?: string | null;
  badge?: string | null;
  isBlocked?: boolean;
  isHidden?: boolean;
  // Subdeck "de revisão" que reaproveita cards de outros subdecks do mesmo
  // campo — com isso true, some dos totais agregados (header, subtotal do
  // grupo) sem deixar de contar normalmente no card dele mesmo.
  excludeFromCount?: boolean;
  // "Restringir temporariamente" — estado independente de isBlocked (ver
  // comentário em models/RyanSpace.js). Enforcement real no backend.
  restricted?: boolean;
  restrictedMessage?: string;
  // "Deixar gratuito" — libera pra qualquer conta logada, sem prazo, até o
  // admin desmarcar. Não bloqueia venda normal do subdeck.
  isFree?: boolean;
  hasRead?: boolean;
  pageCount?: number;
  readPercentage?: number | null;
  lastReadAt?: string | null;
  // Venda granular: pode ser true mesmo com o `comprado` de topo (o campo
  // inteiro) false, se essa aba/subdeck específico foi vendido avulso — ver
  // GET /api/ryan/spaces/:spaceId. Undefined em respostas antigas de cache;
  // trata como "sem info própria", cai pro `comprado` de topo.
  comprado?: boolean;
  // Link da prévia ativa (flashcards ou apostila) — selo "Prévia" pra quem não tem acesso
  previewUrl?: string;
}

const SEM_ABA_TAB_ID = '__no_tab__';

// Nomes prontos pro "Gerenciar Abas" — atalho pra criar uma aba comum de
// matéria médica com um clique, sem digitar. Nem todo campo usa essas
// abas; a lista só oferece o que ainda não existe naquele campo.
const RYAN_TAB_NAME_PRESETS = [
  'Anatomia', 'Histologia', 'Fisiologia', 'Semiologia',
  'Patologias', 'Radiologia', 'Morfopatologia', 'Farmacologia',
];

interface FieldData {
  space: {
    _id: string;
    name: string;
    description?: string;
    colorId: string;
    icon: string;
    theme?: string;
    deckId?: string;
    flashcardsColor?: string;
    apostilasColor?: string;
    typeSectionOrder?: ('flashcards' | 'apostila')[];
    defaultSubdeckColorId?: string | null;
    purchaseUrl?: string;
  };
  tabs: RyanTab[];
  subdecks: SubdeckData[];
  layoutMode?: 'grid' | 'groups' | 'byType';
}

const DEFAULT_FLASHCARDS_COLOR = '#2F7130';
const DEFAULT_APOSTILAS_COLOR = '#B8860B';

const EMPTY_SUBDECK = { name: '', description: '', type: 'flashcards' as 'flashcards' | 'apostila', tabId: '', groupId: '' };
const SEM_ABA_TAB_SANITIZE = (tabId: string) => (tabId === '__no_tab__' ? null : tabId || null);

// ── Sortable subdeck wrapper ──────────────────────────────────────────────────
const SortableSubdeck: React.FC<{
  subdeck: SubdeckData;
  spaceId: string;
  spaceName: string;
  isAdmin: boolean;
  isVip: boolean;
  isEditor: boolean;
  comprado: boolean;
  ryanConfig: RyanConfig;
  onDelete: (subdeckId: string, deckId: string) => void;
  onExport: (deckId: string, subdeckId: string) => void;
  onExportApostila: (deckId: string, subdeckId: string, name: string) => void;
  onMove: (subdeck: SubdeckData) => void;
  onCopy: (subdeck: SubdeckData) => void;
  onMarkRead: (subdeck: SubdeckData) => void;
  onToggleBlock: (subdeck: SubdeckData) => void;
  onToggleHidden: (subdeck: SubdeckData) => void;
  onToggleExcludeCount: (subdeck: SubdeckData) => void;
  onToggleFree: (subdeck: SubdeckData) => void;
  onOpenRestrict: (subdeck: SubdeckData) => void;
  onRemoveRestriction: (subdeck: SubdeckData) => void;
  onEdit: (subdeck: SubdeckData) => void;
  onBlockedClick: (name: string) => void;
  onRestrictedClick: (subdeck: SubdeckData) => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
  onAssignGroup: (subdeck: SubdeckData) => void;
  onAssignCategory: (subdeck: SubdeckData) => void;
  onConfigurePreview: (subdeck: SubdeckData) => void;
  onConfigureApostilaPreview: (subdeck: SubdeckData) => void;
  accentColor?: string;
  selectionMode?: boolean;
  selectedSubdeckIds?: Set<string>;
  onToggleSelect?: (subdeckId: string) => void;
}> = ({
  subdeck, spaceId, spaceName, isAdmin, isVip, isEditor, comprado, ryanConfig,
  onDelete, onExport, onExportApostila, onMove, onCopy,
  onMarkRead, onToggleBlock, onToggleHidden, onToggleExcludeCount, onToggleFree, onOpenRestrict, onRemoveRestriction,
  onEdit, onBlockedClick, onRestrictedClick,
  onToast, onAssignGroup, onAssignCategory, onConfigurePreview, onConfigureApostilaPreview,
  accentColor, selectionMode = false, selectedSubdeckIds, onToggleSelect,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: subdeck._id,
    disabled: !isAdmin || selectionMode,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    height: '100%',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-subdeck-id={subdeck._id}
      className={isDragging ? 'ryan-subdeck-dragging-placeholder' : undefined}
    >
      <RyanSubdeckCard
        subdeck={subdeck}
        spaceId={spaceId}
        spaceName={spaceName}
        isAdmin={isAdmin}
        isVip={isVip}
        isEditor={isEditor}
        comprado={subdeck.comprado ?? comprado}
        ryanConfig={ryanConfig}
        dragHandleAttrs={isAdmin ? { attributes, listeners } : undefined}
        onDelete={() => onDelete(subdeck._id, subdeck.deckId)}
        onExport={() => onExport(subdeck.deckId, subdeck._id)}
        onExportApostila={() => onExportApostila(subdeck.deckId, subdeck._id, subdeck.name)}
        onMove={() => onMove(subdeck)}
        onCopy={() => onCopy(subdeck)}
        onMarkRead={() => onMarkRead(subdeck)}
        onToggleBlock={() => onToggleBlock(subdeck)}
        onToggleHidden={() => onToggleHidden(subdeck)}
        onToggleExcludeCount={() => onToggleExcludeCount(subdeck)}
        onToggleFree={() => onToggleFree(subdeck)}
        onOpenRestrict={() => onOpenRestrict(subdeck)}
        onRemoveRestriction={() => onRemoveRestriction(subdeck)}
        onEdit={() => onEdit(subdeck)}
        onBlockedClick={() => onBlockedClick(subdeck.name)}
        onRestrictedClick={() => onRestrictedClick(subdeck)}
        onAssignGroup={() => onAssignGroup(subdeck)}
        onAssignCategory={() => onAssignCategory(subdeck)}
        onConfigurePreview={() => onConfigurePreview(subdeck)}
        onConfigureApostilaPreview={() => onConfigureApostilaPreview(subdeck)}
        accentColor={accentColor}
        selectionMode={selectionMode}
        isSelected={!!selectedSubdeckIds?.has(subdeck._id)}
        onToggleSelect={onToggleSelect ? () => onToggleSelect(subdeck._id) : undefined}
      />
    </div>
  );
};

// ── Ghost do DragOverlay — clone visual que segue o cursor durante o arrasto,
// já que sem ele o dnd-kit só deixa o card original semitransparente parado
// no lugar, sem nenhuma resposta visual de "para onde estou arrastando". ──
const SubdeckDragGhost: React.FC<{ subdeck: SubdeckData; accentColor: string }> = ({ subdeck, accentColor }) => {
  const IconComp = getRyanMedicalIcon(subdeck.iconId) || (subdeck.type === 'apostila' ? FileText : BookOpen);
  return (
    <div className="ryan-subdeck-drag-ghost" style={{ borderColor: accentColor }}>
      <div className="ryan-subdeck-drag-ghost-icon" style={{ background: accentColor }}>
        <IconComp size={18} />
      </div>
      <span className="ryan-subdeck-drag-ghost-name">{subdeck.name}</span>
    </div>
  );
};

// ── Separador de seção (modo "Por Tipo") ─────────────────────────────────────
// As linhas/pill usavam concatenação de string tipo `${color}90` pra simular
// opacidade — só funciona se `color` for um hex puro de 6 dígitos. Desde que
// as cores de categoria passaram a vir de RYAN_SUBDECK_COLORS (oklch() puro
// ou color-mix(...) aninhado), essa concatenação virava CSS inválido e o
// navegador simplesmente ignorava a declaração — linhas e pill ficavam sem
// cor nenhuma (fundo/borda transparentes, mesmo efeito de "sumiu"). Trocado
// por color-mix(), que aceita qualquer formato de cor válido como entrada.
const RyanTypeSeparator: React.FC<{ name: string; color: string }> = ({ name, color }) => (
  <div className="ryan-type-separator">
    <div
      className="ryan-type-separator-line"
      style={{ background: `linear-gradient(to right, transparent, color-mix(in oklch, ${color} 90%, transparent), ${color})` }}
    />
    <span
      className="ryan-type-separator-name"
      style={{
        color,
        background: `color-mix(in oklch, ${color} 18%, transparent)`,
        borderColor: `color-mix(in oklch, ${color} 40%, transparent)`,
      }}
    >
      {name}
    </span>
    <div
      className="ryan-type-separator-line"
      style={{ background: `linear-gradient(to left, transparent, color-mix(in oklch, ${color} 90%, transparent), ${color})` }}
    />
  </div>
);

// ── Sortable tab row (inside modal) ──────────────────────────────────────────
const SortableTabRow: React.FC<{
  tab: RyanTab;
  renameValue: string;
  onChange: (val: string) => void;
  onRename: () => void;
  onDelete: () => void;
  onToggleBlock: () => void;
}> = ({ tab, renameValue, onChange, onRename, onDelete, onToggleBlock }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tab._id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="ryan-tab-mgmt-row">
      <div className="ryan-tab-drag-handle" {...attributes} {...listeners}>
        <GripVertical size={13} />
      </div>
      <input
        className="ryan-modal-input ryan-tab-mgmt-input"
        value={renameValue}
        onChange={e => onChange(e.target.value)}
      />
      <button
        className="ryan-tab-rename-btn"
        onClick={onRename}
        disabled={!renameValue?.trim() || renameValue === tab.name}
      >
        Salvar
      </button>
      <button
        className={`ryan-tab-block-btn${tab.isBlocked ? ' ryan-tab-block-btn--active' : ''}`}
        onClick={onToggleBlock}
        title={tab.isBlocked ? 'Remover interdição (em obras)' : 'Interditar aba (em obras)'}
      >
        {tab.isBlocked ? <Lock size={13} /> : <Unlock size={13} />}
      </button>
      <button className="ryan-tab-delete-btn" onClick={onDelete}>
        <Trash2 size={13} />
      </button>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const RyanFieldPage: React.FC = () => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, loading: authLoading } = useAuth();
  const { isVip, isEditor } = useRyanAuth();

  // Deep-link vindo da busca do header (RyanHeader.tsx): abre o campo já
  // direto na aba certa e com o card do deck/apostila pesquisado destacado.
  const highlightSubdeckId = (location.state as any)?.highlightSubdeckId as string | undefined;

  const [data, setData] = useState<FieldData | null>(null);
  const [subdecksState, setSubdecksState] = useState<SubdeckData[]>([]);
  // Card sendo arrastado no momento (grid de subdecks) — alimenta o DragOverlay,
  // já que sem ele o dnd-kit só deixa o card original semitransparente no
  // lugar, sem nenhuma resposta visual clara de "isso está sendo arrastado".
  const [activeSubdeckId, setActiveSubdeckId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTabId, setActiveTabId] = useState<string>(
    () => spaceId ? sessionStorage.getItem(`ryan_tab_${spaceId}`) || '' : ''
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [showRankingModal, setShowRankingModal] = useState(false);
  const [comprado, setComprado] = useState(false);
  const [ryanConfig, setRyanConfig] = useState<RyanConfig>({
    whatsappNumber: '5577988362587',
    whatsappMessage: 'Olá! Tenho interesse em adquirir o material {campo}. Poderia me passar mais informações?',
  });

  // Layout mode
  const [layoutMode, setLayoutMode] = useState<'grid' | 'groups' | 'byType'>('grid');
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [flashcardsColor, setFlashcardsColor] = useState(DEFAULT_FLASHCARDS_COLOR);
  const [apostilasColor, setApostilasColor] = useState(DEFAULT_APOSTILAS_COLOR);
  const [typeSectionOrder, setTypeSectionOrder] = useState<('flashcards' | 'apostila')[]>(['flashcards', 'apostila']);
  // Cor com que subdecks NOVOS deste campo nascem (criar/importar) — ver "Layout da Página"
  const [defaultSubdeckColorId, setDefaultSubdeckColorId] = useState<string | null>(null);
  // Link do botão "Quero adquirir" do modal de compra (rascunho editado em "Layout da Página")
  const [purchaseUrlDraft, setPurchaseUrlDraft] = useState('');
  const [purchaseUrlSaving, setPurchaseUrlSaving] = useState(false);

  // Categorias de exibição do modo "Por Tipo" (além de Flashcards/Apostilas, admin pode criar customizadas)
  const [displayCategories, setDisplayCategories] = useState<RyanDisplayCategoryData[]>([]);
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);
  const [showAssignCategoryModal, setShowAssignCategoryModal] = useState(false);
  const [subdeckForCategory, setSubdeckForCategory] = useState<SubdeckData | null>(null);

  // Seleção múltipla de subdecks (mover entre abas em lote)
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedSubdeckIds, setSelectedSubdeckIds] = useState<Set<string>>(new Set());
  const [showMoveTabModal, setShowMoveTabModal] = useState(false);
  const [movingTab, setMovingTab] = useState(false);

  // Groups
  const [groups, setGroups] = useState<RyanSubdeckGroupData[]>([]);
  const [, setGroupsLoading] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    if (!spaceId) return new Set();
    try {
      const saved = localStorage.getItem(`ryan_groups_open_${spaceId}`);
      if (saved) return new Set(JSON.parse(saved));
    } catch {}
    return new Set();
  });

  // Group modals
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showManageGroupsModal, setShowManageGroupsModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<RyanSubdeckGroupData | null>(null);
  const [showAssignGroupModal, setShowAssignGroupModal] = useState(false);
  const [subdeckForGroup, setSubdeckForGroup] = useState<SubdeckData | null>(null);

  // Tab management modal
  const [showTabModal, setShowTabModal] = useState(false);
  const [tabRenames, setTabRenames] = useState<Record<string, string>>({});
  const [sortedTabs, setSortedTabs] = useState<RyanTab[]>([]);
  const [newTabName, setNewTabName] = useState('');
  const [tabSaving, setTabSaving] = useState(false);

  // Add subdeck modal
  const [showSubdeckModal, setShowSubdeckModal] = useState(false);
  const [subdeckForm, setSubdeckForm] = useState(EMPTY_SUBDECK);
  const [subdeckSaving, setSubdeckSaving] = useState(false);
  const [subdeckError, setSubdeckError] = useState('');

  // Import subdeck (flashcards)
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState('');
  const [importGroupId, setImportGroupId] = useState('');

  // Import apostila
  const [showImportApostilaModal, setShowImportApostilaModal] = useState(false);

  // Interdicted modal
  const [showInterdictedModal, setShowInterdictedModal] = useState(false);
  const [interdictedName, setInterdictedName] = useState('');

  // "Restringir temporariamente" — modal admin de configuração (subdeck sendo
  // restringido/reconfigurado, ou null = fechado) + modal exibido pra usuário
  // comum que tentou acessar um subdeck restricted (independente do de "Interditado" acima).
  const [restrictingSubdeck, setRestrictingSubdeck] = useState<SubdeckData | null>(null);
  const [restrictSaving, setRestrictSaving] = useState(false);
  const [restrictedClickInfo, setRestrictedClickInfo] = useState<{ name: string; message: string } | null>(null);

  // Move / Copy modals
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [subdeckToMove, setSubdeckToMove] = useState<SubdeckData | null>(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [subdeckToCopy, setSubdeckToCopy] = useState<SubdeckData | null>(null);

  // Edit subdeck modal
  const [editingSubdeck, setEditingSubdeck] = useState<SubdeckData | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', colorId: 'floresta', iconId: null as string | null, badge: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Flashcard preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [subdeckForPreview, setSubdeckForPreview] = useState<SubdeckData | null>(null);

  // Apostila preview modal
  const [showApostilaPreviewModal, setShowApostilaPreviewModal] = useState(false);
  const [subdeckForApostilaPreview, setSubdeckForApostilaPreview] = useState<SubdeckData | null>(null);

  // Tab animation key
  const [tabKey, setTabKey] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Persist open groups in localStorage
  useEffect(() => {
    if (!spaceId) return;
    localStorage.setItem(`ryan_groups_open_${spaceId}`, JSON.stringify([...openGroups]));
  }, [openGroups, spaceId]);

  const toggleGroup = (groupId: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const loadGroups = useCallback(async () => {
    if (!spaceId) return;
    setGroupsLoading(true);
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setGroups(await res.json());
    } finally {
      setGroupsLoading(false);
    }
  }, [spaceId]);

  // Leitura aberta a qualquer usuário (não só admin) — necessária pra renderizar
  // corretamente as seções do modo 'byType', não só pra tela de administração delas
  const loadDisplayCategories = useCallback(async () => {
    if (!spaceId) return;
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`/api/ryan/spaces/${spaceId}/display-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setDisplayCategories(await res.json());
    } catch { /* non-critical */ }
  }, [spaceId]);

  const loadField = useCallback(async () => {
    if (!spaceId) return;
    const token = localStorage.getItem('accessToken');
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/ryan/spaces/${spaceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        // Campo "restringido temporariamente" — ao contrário de isBlocked
        // (abaixo), o servidor já recusa o conteúdo (403) pra usuário comum
        // que tentar entrar direto pela URL. Redireciona pro Início mostrando
        // a mensagem própria do admin, não um erro genérico.
        if (res.status === 403) {
          const e = await res.json().catch(() => ({}));
          if (e.reason === 'restricted') {
            navigate('/ryan', {
              replace: true,
              state: { restrictedSpaceName: e.spaceName || '', restrictedSpaceMessage: e.restrictedMessage || '' },
            });
            return;
          }
        }
        throw new Error('Campo não encontrado');
      }
      const d = await res.json();

      // Campo interditado ("em obras") — mesmo padrão de subdeck/resumo
      // bloqueado: não-admin nunca vê o conteúdo, mesmo entrando direto pela
      // URL (não só pelo card do RyanHomePage). Admin navega normalmente.
      if (d.space?.isBlocked && !isAdmin) {
        navigate('/ryan', { replace: true, state: { blockedSpaceName: d.space.name } });
        return;
      }

      let subdecksWithMeta: SubdeckData[] = d.subdecks || [];
      const apostilas = subdecksWithMeta.filter(s => s.type === 'apostila' && s.hasResumo);
      if (apostilas.length > 0 && apostilas[0]?.deckId) {
        try {
          const ids = apostilas.map(s => s._id).join(',');
          const metaRes = await fetch(
            `/api/decks/${apostilas[0].deckId}/subdecks-resumo-meta?ids=${ids}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (metaRes.ok) {
            const metaData = await metaRes.json();
            subdecksWithMeta = subdecksWithMeta.map(s => ({
              ...s,
              hasRead: metaData.meta?.[s._id]?.hasRead,
              pageCount: metaData.meta?.[s._id]?.pageCount,
              readPercentage: metaData.meta?.[s._id]?.readPercentage,
              lastReadAt: metaData.meta?.[s._id]?.lastReadAt,
            }));
          }
        } catch { /* non-critical */ }
      }

      // Selo "Prévia": só quem não tem acesso a algum subdeck precisa saber das prévias ativas
      const hasLockedSubdeck = !d.comprado || subdecksWithMeta.some(s => s.comprado === false);
      if (!isAdmin && hasLockedSubdeck) {
        try {
          const previewRes = await fetch(`/api/ryan/spaces/${spaceId}/previews`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (previewRes.ok) {
            const { previews } = await previewRes.json();
            if (previews) {
              subdecksWithMeta = subdecksWithMeta.map(s =>
                previews[s._id] ? { ...s, previewUrl: previews[s._id] } : s
              );
            }
          }
        } catch { /* non-critical */ }
      }

      setData(d);
      setComprado(d.comprado || false);
      setSubdecksState(subdecksWithMeta);
      setLayoutMode(d.layoutMode || 'grid');
      setFlashcardsColor(d.space?.flashcardsColor || DEFAULT_FLASHCARDS_COLOR);
      setApostilasColor(d.space?.apostilasColor || DEFAULT_APOSTILAS_COLOR);
      setDefaultSubdeckColorId(d.space?.defaultSubdeckColorId || null);
      setPurchaseUrlDraft(d.space?.purchaseUrl || '');
      setTypeSectionOrder(
        Array.isArray(d.space?.typeSectionOrder) && d.space.typeSectionOrder.length === 2
          ? d.space.typeSectionOrder
          : ['flashcards', 'apostila']
      );

      if (d.tabs?.length) {
        const sorted = [...d.tabs].sort((a: RyanTab, b: RyanTab) => a.order - b.order);
        // Primeira aba NÃO interditada, na ordem — cai na última (sorted[0])
        // só se todas estiverem interditadas.
        const firstFreeTab = sorted.find((t: RyanTab) => !t.isBlocked) || sorted[0];
        setActiveTabId(prev => {
          if (prev === SEM_ABA_TAB_ID) return SEM_ABA_TAB_ID;
          const validIds = new Set(sorted.map((t: RyanTab) => t._id));
          const prevTab = prev ? sorted.find((t: RyanTab) => t._id === prev) : null;
          // Aba lembrada (sessionStorage) de uma visita anterior que virou
          // interditada nesse meio tempo não pode continuar sendo a aba
          // padrão — redireciona pra primeira aba livre, como numa visita nova.
          if (!prev || !validIds.has(prev) || prevTab?.isBlocked) return firstFreeTab._id;
          return prev;
        });
      } else {
        setActiveTabId('');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [spaceId, isAdmin, navigate]);

  // Espera a sessão ser verificada: depois de um recarregamento completo (ex.: o
  // "voltar" da apostila usa window.location), isAdmin começa false enquanto o
  // AuthContext carrega — e o admin num campo interditado era mandado pro Início.
  useEffect(() => { if (!authLoading) loadField(); }, [loadField, authLoading]);

  useEffect(() => {
    if (isAdmin && spaceId) loadGroups();
  }, [isAdmin, spaceId, loadGroups]);

  useEffect(() => {
    if (spaceId) loadDisplayCategories();
  }, [spaceId, loadDisplayCategories]);

  useEffect(() => {
    if (spaceId && activeTabId) {
      sessionStorage.setItem(`ryan_tab_${spaceId}`, activeTabId);
    }
  }, [activeTabId, spaceId]);

  // Seleção é escopada à aba atual — evita mover por engano subdecks de outra aba
  // que ficaram selecionados antes da troca
  useEffect(() => {
    setSelectedSubdeckIds(new Set());
    setSearchQuery('');
  }, [activeTabId]);

  // Deep-link da busca: troca pra aba do deck/apostila pesquisado (se
  // necessário) e, depois que o card estiver renderizado, rola até ele e
  // aplica um destaque temporário. Implementado via DOM direto (data-attribute
  // no wrapper do card) pra não precisar passar mais uma prop por toda a
  // cadeia de SortableSubdeck → RyanSubdeckCard.
  //
  // highlightProcessedRef marca quando esse deep-link já foi tratado uma vez.
  // Sem isso, highlightSubdeckId nunca muda (vem de location.state, que não
  // é limpo) — então TODA troca de aba feita manualmente pelo usuário depois
  // disparava esse mesmo efeito de novo, via a dependência activeTabId, e ele
  // forçava a aba de volta pra targetTabId, prendendo o usuário na aba de
  // chegada pra sempre. Uma vez marcado como processado, o efeito vira no-op
  // e a navegação por abas volta a ser livre.
  const highlightProcessedRef = useRef(false);

  useEffect(() => {
    if (loading || !highlightSubdeckId || subdecksState.length === 0) return;
    if (highlightProcessedRef.current) return;

    const target = subdecksState.find(s => s._id === highlightSubdeckId);
    if (!target) return;

    const targetTabId = target.tabId || SEM_ABA_TAB_ID;
    if (activeTabId !== targetTabId) {
      setActiveTabId(targetTabId);
      return;
    }

    highlightProcessedRef.current = true;

    const t = setTimeout(() => {
      const el = document.querySelector(`[data-subdeck-id="${highlightSubdeckId}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ryan-subdeck-highlight-pulse');
      setTimeout(() => el.classList.remove('ryan-subdeck-highlight-pulse'), 2200);
    }, 300);
    return () => clearTimeout(t);
  }, [loading, highlightSubdeckId, subdecksState, activeTabId]);

  useEffect(() => {
    if (showTabModal && data?.tabs) {
      const sorted = [...data.tabs].sort((a, b) => a.order - b.order);
      setSortedTabs(sorted);
    }
  }, [data?.tabs, showTabModal]);

  useEffect(() => {
    const tok = localStorage.getItem('accessToken');
    fetch('/api/ryan/config', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(cfg => { if (cfg) setRyanConfig(cfg); })
      .catch(() => {});
  }, []);

  const openTabModal = () => {
    const sorted = [...(data?.tabs || [])].sort((a, b) => a.order - b.order);
    setSortedTabs(sorted);
    const initial: Record<string, string> = {};
    sorted.forEach(t => { initial[t._id] = t.name; });
    setTabRenames(initial);
    setNewTabName('');
    setShowTabModal(true);
  };

  // ── Layout mode ──
  const openLayoutModal = () => {
    setShowLayoutModal(true);
  };

  const handleSetLayoutMode = async (
    mode: 'grid' | 'groups' | 'byType',
    options?: { flashcardsColor: string; apostilasColor: string; typeSectionOrder: ('flashcards' | 'apostila')[] }
  ) => {
    if (!spaceId || layoutSaving) return;
    setLayoutSaving(true);
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/layout`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ layoutMode: mode, ...options }),
      });
      if (res.ok) {
        setLayoutMode(mode);
        if (options) {
          setFlashcardsColor(options.flashcardsColor);
          setApostilasColor(options.apostilasColor);
          setTypeSectionOrder(options.typeSectionOrder);
          // A ordem binária pode ter re-sincronizado o `order` das 2 categorias padrão no backend
          loadDisplayCategories();
        }
        setToast({
          message: mode === 'groups' ? 'Modo grupos ativado!' : mode === 'byType' ? 'Modo por tipo ativado!' : 'Modo grid ativado!',
          type: 'success',
        });
        setShowLayoutModal(false);
      } else {
        const e = await res.json();
        setToast({ message: e.message || 'Erro ao salvar layout', type: 'error' });
      }
    } finally {
      setLayoutSaving(false);
    }
  };

  const handleSavePurchaseUrl = async () => {
    if (!spaceId || purchaseUrlSaving) return;
    setPurchaseUrlSaving(true);
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/purchase-url`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ purchaseUrl: purchaseUrlDraft.trim() }),
      });
      const body = await res.json().catch(() => ({} as any));
      if (res.ok) {
        setPurchaseUrlDraft(body.purchaseUrl || '');
        setData(prev => prev ? { ...prev, space: { ...prev.space, purchaseUrl: body.purchaseUrl || '' } } : prev);
        setToast({ message: body.purchaseUrl ? 'Link de compra salvo!' : 'Link de compra removido', type: 'success' });
      } else {
        setToast({ message: body.message || 'Erro ao salvar link de compra', type: 'error' });
      }
    } catch {
      setToast({ message: 'Erro de conexão', type: 'error' });
    } finally {
      setPurchaseUrlSaving(false);
    }
  };

  const handleSetDefaultSubdeckColor = async (colorId: string) => {
    if (!spaceId || layoutSaving) return;
    setLayoutSaving(true);
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/layout`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ layoutMode, defaultSubdeckColorId: colorId }),
      });
      if (res.ok) {
        setDefaultSubdeckColorId(colorId);
        setToast({ message: 'Cor padrão dos subdecks atualizada!', type: 'success' });
      } else {
        const e = await res.json();
        setToast({ message: e.message || 'Erro ao salvar cor padrão', type: 'error' });
      }
    } finally {
      setLayoutSaving(false);
    }
  };

  // ── Tab drag ──
  const handleTabDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIdx = sortedTabs.findIndex(t => t._id === active.id);
    const newIdx = sortedTabs.findIndex(t => t._id === over.id);
    const reordered = arrayMove(sortedTabs, oldIdx, newIdx);
    setSortedTabs(reordered);

    const token = localStorage.getItem('accessToken');
    await fetch(`/api/ryan/admin/spaces/${spaceId}/tabs/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ tabs: reordered.map((t, i) => ({ _id: t._id, order: i })) }),
    });
    loadField();
  };

  // ── Tab CRUD ──
  // `quickName` vem dos atalhos de nome pronto (RYAN_TAB_NAME_PRESETS) — usa
  // esse nome direto em vez do input, sem precisar passar por setNewTabName
  // (evitaria uma corrida com o estado assíncrono do React).
  const handleCreateTab = async (quickName?: string) => {
    const name = (quickName ?? newTabName).trim();
    if (!name || tabSaving) return;
    setTabSaving(true);
    const token = localStorage.getItem('accessToken');
    try {
      await fetch(`/api/ryan/admin/spaces/${spaceId}/tabs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      if (!quickName) setNewTabName('');
      const res = await fetch(`/api/ryan/spaces/${spaceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const fresh = await res.json();
        setData(fresh);
        setSubdecksState(fresh.subdecks || []);
        const sorted = [...fresh.tabs].sort((a: RyanTab, b: RyanTab) => a.order - b.order);
        setSortedTabs(sorted);
        const renames: Record<string, string> = {};
        sorted.forEach((t: RyanTab) => { renames[t._id] = t.name; });
        setTabRenames(renames);
        setActiveTabId(prev => {
          const validIds = new Set(sorted.map((t: RyanTab) => t._id));
          return (!prev || !validIds.has(prev)) ? sorted[0]._id : prev;
        });
      }
    } finally {
      setTabSaving(false);
    }
  };

  const handleRenameTab = async (tabId: string, originalName: string) => {
    const newName = tabRenames[tabId];
    if (!newName?.trim() || newName === originalName) return;
    const token = localStorage.getItem('accessToken');
    await fetch(`/api/ryan/admin/spaces/${spaceId}/tabs/${tabId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name: newName.trim() }),
    });
    loadField();
  };

  const handleToggleTabBlock = async (tabId: string, isBlocked: boolean) => {
    const token = localStorage.getItem('accessToken');
    await fetch(`/api/ryan/admin/spaces/${spaceId}/tabs/${tabId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ isBlocked: !isBlocked }),
    });
    loadField();
  };

  const handleDeleteTab = async (tabId: string) => {
    if (!window.confirm('Excluir esta aba? Os decks ficarão sem aba.')) return;
    const token = localStorage.getItem('accessToken');
    await fetch(`/api/ryan/admin/spaces/${spaceId}/tabs/${tabId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (activeTabId === tabId) setActiveTabId('');
    setTabRenames(r => { const copy = { ...r }; delete copy[tabId]; return copy; });
    loadField();
  };

  // ── Subdeck drag (grid mode) ──
  const handleSubdeckDragStart = (event: DragStartEvent) => {
    setActiveSubdeckId(event.active.id as string);
  };

  const handleSubdeckDragEnd = async (event: DragEndEvent) => {
    setActiveSubdeckId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const visibleIds = new Set(filteredSubdecks.map(s => s._id));
    const oldIdx = filteredSubdecks.findIndex(s => s._id === active.id);
    const newIdx = filteredSubdecks.findIndex(s => s._id === over.id);
    const newVisible = arrayMove(filteredSubdecks, oldIdx, newIdx);

    const fullList = [...subdecksState];
    const visiblePositions: number[] = [];
    fullList.forEach((s, i) => { if (visibleIds.has(s._id)) visiblePositions.push(i); });
    visiblePositions.forEach((pos, i) => { fullList[pos] = newVisible[i]; });
    setSubdecksState(fullList);

    const token = localStorage.getItem('accessToken');
    await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        subdeckRefs: fullList.map(s => ({ subdeckId: s._id, tabId: s.tabId })),
      }),
    });
  };

  // ── Categorias de exibição (modo "Por Tipo") ──
  // Subdeck sem displayCategoryId próprio cai na categoria padrão do seu type funcional
  const flashcardsDefaultCategoryId = displayCategories.find(c => c.isDefault && c.defaultForType === 'flashcards')?._id || null;
  const apostilaDefaultCategoryId = displayCategories.find(c => c.isDefault && c.defaultForType === 'apostila')?._id || null;

  // Sempre retorna um id não-nulo: enquanto displayCategories ainda não carregou (estado
  // transitório no mount), cai de volta pro literal 'flashcards'/'apostila' — o mesmo id
  // usado pelas seções de fallback no render, pra manter render e drag-and-drop consistentes
  const resolveCategoryId = (s: SubdeckData): string => {
    if (s.displayCategoryId) return s.displayCategoryId;
    if (s.type === 'apostila') return apostilaDefaultCategoryId || 'apostila';
    return flashcardsDefaultCategoryId || 'flashcards';
  };

  // ── Subdeck drag (modo "Por Tipo" — reordena dentro da própria seção só) ──
  const handleTypeSectionDragEnd = async (event: DragEndEvent, categoryId: string) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sectionSubdecks = filteredSubdecks.filter(s => resolveCategoryId(s) === categoryId);
    const oldIdx = sectionSubdecks.findIndex(s => s._id === active.id);
    const newIdx = sectionSubdecks.findIndex(s => s._id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reorderedSection = arrayMove(sectionSubdecks, oldIdx, newIdx);

    const reorderedIds = new Set(reorderedSection.map(s => s._id));
    const sectionPositionsInVisible: number[] = [];
    filteredSubdecks.forEach((s, i) => { if (reorderedIds.has(s._id)) sectionPositionsInVisible.push(i); });
    const newVisible = [...filteredSubdecks];
    sectionPositionsInVisible.forEach((pos, i) => { newVisible[pos] = reorderedSection[i]; });

    const visibleIds = new Set(newVisible.map(s => s._id));
    const fullList = [...subdecksState];
    const fullPositions: number[] = [];
    fullList.forEach((s, i) => { if (visibleIds.has(s._id)) fullPositions.push(i); });
    fullPositions.forEach((pos, i) => { fullList[pos] = newVisible[i]; });
    setSubdecksState(fullList);

    const token = localStorage.getItem('accessToken');
    await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        subdeckRefs: fullList.map(s => ({ subdeckId: s._id, tabId: s.tabId })),
      }),
    });
  };

  // ── Seleção múltipla de subdecks ──
  const toggleSubdeckSelection = (id: string) => {
    setSelectedSubdeckIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const enterSelectionMode = () => {
    setSelectionMode(true);
    setSelectedSubdeckIds(new Set());
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedSubdeckIds(new Set());
  };

  const handleBulkMoveToTab = async (newTabId: string | null) => {
    if (selectedSubdeckIds.size === 0 || movingTab) return;
    setMovingTab(true);
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/move-tab`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subdeckIds: [...selectedSubdeckIds], newTabId }),
      });
      if (!res.ok) {
        const e = await res.json();
        setToast({ message: e.message || 'Erro ao mover decks', type: 'error' });
        return;
      }
      const result = await res.json();
      const tabName = newTabId
        ? (data?.tabs.find(t => t._id === newTabId)?.name || 'aba selecionada')
        : 'Sem aba';
      let message = `${result.movedCount} deck${result.movedCount !== 1 ? 's' : ''} movido${result.movedCount !== 1 ? 's' : ''} para ${tabName}`;
      if (result.ungroupedCount > 0) {
        message += ` — ${result.ungroupedCount} perdeu(ram) o vínculo de grupo por causa da troca de aba`;
      }
      setToast({ message, type: 'success' });
      setShowMoveTabModal(false);
      exitSelectionMode();
      loadField();
    } catch {
      setToast({ message: 'Erro de conexão', type: 'error' });
    } finally {
      setMovingTab(false);
    }
  };

  // ── Study group ──
  // ── Continuar de onde parou (hero de progresso) — mesmo destino do botão
  // "Estudar" de um card de subdeck individual (RyanSubdeckCard.tsx: goStudy).
  const handleContinueStudy = useCallback((subdeck: { _id: string; deckId: string }) => {
    sessionStorage.setItem('graia_theme_context', 'ryan');
    navigate(`/study/${subdeck.deckId}/${subdeck._id}`, {
      state: {
        isPurchasedDeck: true,
        fromRyan: true,
        returnPath: `/ryan/campo/${spaceId}`,
        spaceName: data?.space.name || '',
        spaceId,
      },
    });
  }, [navigate, spaceId, data?.space.name]);

  const handleContinueApostila = useCallback((subdeck: { _id: string; deckId: string }) => {
    navigate(`/resumo/${subdeck.deckId}/${subdeck._id}`, {
      state: {
        isPurchasedDeck: true,
        fromRyan: true,
        returnPath: `/ryan/campo/${spaceId}`,
        spaceName: data?.space.name || '',
        spaceId,
      },
    });
  }, [navigate, spaceId, data?.space.name]);

  const handleStudyGroup = useCallback((group: RyanSubdeckGroupData, groupSubdecks: SubdeckData[]) => {
    const flashcardSubdecks = groupSubdecks.filter(s => s.type !== 'apostila' && s.cardCount > 0);
    if (flashcardSubdecks.length === 0) {
      setToast({ message: 'Nenhum card neste grupo para estudar.', type: 'info' });
      return;
    }
    const pool = flashcardSubdecks.map(s => ({ deckId: s.deckId, subdeckId: s._id, cycleId: '' }));
    localStorage.setItem('customStudyPool', JSON.stringify(pool));
    sessionStorage.setItem('graia_theme_context', 'ryan');
    navigate('/study/custom', {
      state: {
        fromRyan: true,
        returnPath: `/ryan/campo/${spaceId}`,
        spaceName: data?.space.name || '',
        spaceId,
        subdeckName: group.name,
        customStudyLabel: group.name,
      },
    });
  }, [spaceId, data?.space.name, navigate]);

  // ── Configure flashcard preview ──
  const handleConfigurePreview = useCallback((subdeck: SubdeckData) => {
    setSubdeckForPreview(subdeck);
    setShowPreviewModal(true);
  }, []);

  // ── Configure apostila preview ──
  const handleConfigureApostilaPreview = useCallback((subdeck: SubdeckData) => {
    setSubdeckForApostilaPreview(subdeck);
    setShowApostilaPreviewModal(true);
  }, []);

  // ── Move group to another tab ──
  const handleMoveGroup = useCallback(async (group: RyanSubdeckGroupData, newTabId: string | null) => {
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/groups/${group._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: group.name,
          description: group.description || '',
          iconName: group.iconName,
          color: group.color,
          tabId: newTabId,
        }),
      });
      if (res.ok) {
        setToast({ message: 'Grupo movido!', type: 'success' });
        await Promise.all([loadGroups(), loadField()]);
      } else {
        setToast({ message: 'Erro ao mover grupo', type: 'error' });
      }
    } catch {
      setToast({ message: 'Erro de conexão', type: 'error' });
    }
  }, [spaceId, loadGroups, loadField]);

  // ── Subdeck drag (group mode) ──
  const handleGroupSubdecksReorder = async (groupId: string | null, reordered: SubdeckData[]) => {
    const reorderedIds = new Set(reordered.map(s => s._id));
    const fullList = [
      ...reordered,
      ...subdecksState.filter(s => !reorderedIds.has(s._id)),
    ];
    setSubdecksState(fullList);

    const token = localStorage.getItem('accessToken');
    await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        subdeckRefs: fullList.map(s => ({ subdeckId: s._id, tabId: s.tabId })),
      }),
    });
  };

  // ── Add subdeck ──
  const openSubdeckModal = () => {
    const initTabId = activeTabId === SEM_ABA_TAB_ID ? '' : activeTabId || '';
    setSubdeckForm({ ...EMPTY_SUBDECK, tabId: initTabId, groupId: '' });
    setSubdeckError('');
    setShowSubdeckModal(true);
  };

  const handleAddSubdeck = async () => {
    if (!subdeckForm.name.trim()) { setSubdeckError('Nome obrigatório'); return; }
    setSubdeckSaving(true);
    setSubdeckError('');
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: subdeckForm.name.trim(),
          description: subdeckForm.description,
          type: subdeckForm.type,
          tabId: SEM_ABA_TAB_SANITIZE(subdeckForm.tabId),
          groupId: subdeckForm.groupId || null,
        }),
      });
      const body = await res.json();
      if (!res.ok) { setSubdeckError(body.message || 'Erro ao criar deck'); return; }
      setShowSubdeckModal(false);
      setSubdeckForm(EMPTY_SUBDECK);
      loadField();
    } catch {
      setSubdeckError('Erro de conexão');
    } finally {
      setSubdeckSaving(false);
    }
  };

  // ── Export cards ──
  const handleExport = async (deckId: string, subdeckId: string) => {
    try {
      await exportImportService.exportSubdeck(deckId, subdeckId);
      setToast({ message: 'Cards exportados!', type: 'success' });
    } catch {
      setToast({ message: 'Erro ao exportar cards', type: 'error' });
    }
  };

  // ── Import cards ──
  const importSubdeckFromFile = async (
    file: File, deckId: string, token: string, onProgress?: (msg: string) => void
  ) => {
    // .json ou .zip (JSON + imagens) — leitura e resolução de imagens compartilhadas com o
    // exportImportService; a criação do subdeck segue pela rota própria do campo (Ryan)
    const { data: rawData, zip } = await exportImportService.readImportFile(file);
    if (!rawData.name || !rawData.cards) throw new Error('Arquivo JSON inválido: falta nome ou cards');
    const fileData = await exportImportService.resolveImportImages(rawData, zip, onProgress);

    const r = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        name: fileData.name,
        description: fileData.description || '',
        type: fileData.type || 'flashcards',
        tabId: SEM_ABA_TAB_SANITIZE(activeTabId),
        groupId: importGroupId || null,
      }),
    });
    if (!r.ok) {
      const e = await r.json();
      throw new Error(e.message || 'Erro ao criar deck');
    }
    const created = await r.json();
    const newSubdeckId = created.subdeck._id;

    if (fileData.cards?.length > 0) {
      let saveError: string | null = null;
      try {
        const put = await fetch(`/api/decks/${deckId}/subdecks/${newSubdeckId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            name: fileData.name,
            description: fileData.description || '',
            references: fileData.references || '',
            cards: fileData.cards.map((c: any) => ({
              front: c.front,
              back: c.back,
              supportContent: c.supportContent || '',
              references: c.references || '',
              media: c.media || { images: [], audios: [] },
            })),
          }),
        });
        if (!put.ok) {
          const e = await put.json().catch(() => ({} as any));
          saveError = e.message || `HTTP ${put.status}`;
        }
      } catch (err: any) {
        saveError = err?.message || 'erro de rede';
      }

      if (saveError) {
        // URLs que resolveImportImages trocou (caminho do zip → secure_url) = imagens já enviadas
        const uploadedUrls = Array.from(new Set<string>(
          fileData.cards.flatMap((c: any, i: number) =>
            (c.media?.images || [])
              .filter((img: any, j: number) => img.url !== rawData.cards[i]?.media?.images?.[j]?.url)
              .map((img: any) => img.url)
          )
        ));
        if (uploadedUrls.length > 0) {
          console.error(
            '❌ [IMPORT] Gravação dos cards falhou. Imagens já enviadas ao Cloudinary (para limpeza manual):',
            uploadedUrls
          );
        }
        const removed = await removeImportedSubdeck(deckId, newSubdeckId, token);
        throw new Error(
          `Erro ao salvar os cards: ${saveError}` +
          (removed ? '' : ' — o deck vazio criado não pôde ser removido, exclua manualmente')
        );
      }
    }
  };

  // Desfaz a criação do subdeck quando a gravação dos cards falha — mesma sequência do handleDeleteSubdeck
  const removeImportedSubdeck = async (deckId: string, subdeckId: string, token: string): Promise<boolean> => {
    try {
      const r1 = await fetch(`/api/decks/${deckId}/subdecks/${subdeckId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r1.ok) return false;
      const r2 = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeckId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      return r2.ok;
    } catch {
      return false;
    }
  };

  const handleImportSubdeck = async () => {
    if (!importFiles.length) return;
    const deckId = data?.space.deckId || subdecksState[0]?.deckId;
    if (!deckId) {
      setToast({ message: 'Deck interno não encontrado', type: 'error' });
      return;
    }
    setImporting(true);
    const token = localStorage.getItem('accessToken') || '';
    let successCount = 0;
    const failed: string[] = [];
    for (const file of importFiles) {
      try {
        // Com vários arquivos, o progresso indica qual deles está sendo processado
        const onProgress = importFiles.length > 1
          ? (msg: string) => setImportProgress(`${file.name}: ${msg}`)
          : setImportProgress;
        await importSubdeckFromFile(file, deckId, token, onProgress);
        successCount++;
      } catch (e: any) {
        failed.push(e?.message ? `${file.name} (${e.message})` : file.name);
      }
    }
    setShowImportModal(false);
    setImportFiles([]);
    setImporting(false);
    setImportProgress('');
    if (failed.length === 0) {
      setToast({
        message: successCount > 1 ? `${successCount} decks importados!` : 'Deck importado!',
        type: 'success'
      });
    } else {
      setToast({
        message: `${successCount} importado(s), ${failed.length} falhou(aram): ${failed.join(', ')}`,
        type: successCount > 0 ? 'info' : 'error'
      });
    }
    loadField();
  };

  // ── Export apostila ──
  const handleExportApostila = async (deckId: string, subdeckId: string, subdeckName: string) => {
    try {
      const { resumo } = await resumoService.getResumo(deckId, subdeckId);
      resumoService.exportApostila(resumo, resumo.pages || [], data?.space.name || '', subdeckName);
      setToast({ message: 'Apostila exportada!', type: 'success' });
    } catch {
      setToast({ message: 'Erro ao exportar apostila', type: 'error' });
    }
  };

  // ── Mark apostila as read ──
  const handleMarkRead = async (subdeck: SubdeckData) => {
    try {
      const token = localStorage.getItem('accessToken') || '';
      const r = await fetch(
        `/api/decks/${subdeck.deckId}/subdecks/${subdeck._id}/resumo/mark-read`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const d = await r.json();
        setSubdecksState(prev =>
          prev.map(s => s._id === subdeck._id ? { ...s, hasRead: d.hasRead } : s)
        );
        setToast({ message: d.message || 'Status atualizado!', type: 'success' });
      }
    } catch {
      setToast({ message: 'Erro ao atualizar leitura', type: 'error' });
    }
  };

  // ── Toggle block ──
  const handleToggleBlock = async (subdeck: SubdeckData) => {
    try {
      const token = localStorage.getItem('accessToken') || '';
      const r = await fetch(
        `/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeck._id}/toggle-block`,
        { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const d = await r.json();
        setSubdecksState(prev =>
          prev.map(s => s._id === subdeck._id ? { ...s, isBlocked: d.isBlocked } : s)
        );
        setToast({ message: d.isBlocked ? 'Deck interditado!' : 'Interdição removida!', type: 'info' });
      }
    } catch {
      setToast({ message: 'Erro ao alterar interdição', type: 'error' });
    }
  };

  // ── Toggle hidden ──
  const handleToggleHidden = async (subdeck: SubdeckData) => {
    try {
      const token = localStorage.getItem('accessToken') || '';
      const r = await fetch(
        `/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeck._id}/toggle-hidden`,
        { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const result = await r.json();
        setSubdecksState(prev =>
          prev.map(s => s._id === subdeck._id ? { ...s, isHidden: result.isHidden } : s)
        );
        setToast({
          message: result.isHidden ? 'Deck ocultado (só você vê)' : 'Deck visível para todos',
          type: 'info',
        });
      }
    } catch {
      setToast({ message: 'Erro ao alterar visibilidade', type: 'error' });
    }
  };

  // ── Toggle "não contar no header" (subdeck de revisão com cards reaproveitados) ──
  const handleToggleExcludeCount = async (subdeck: SubdeckData) => {
    try {
      const token = localStorage.getItem('accessToken') || '';
      const r = await fetch(
        `/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeck._id}/toggle-exclude-count`,
        { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const result = await r.json();
        setSubdecksState(prev =>
          prev.map(s => s._id === subdeck._id ? { ...s, excludeFromCount: result.excludeFromCount } : s)
        );
        setToast({
          message: result.excludeFromCount ? 'Não conta mais no header do campo' : 'Volta a contar no header do campo',
          type: 'info',
        });
      }
    } catch {
      setToast({ message: 'Erro ao alterar contagem', type: 'error' });
    }
  };

  // ── "Deixar gratuito" (libera pra qualquer conta logada, sem prazo) ──
  const handleToggleFree = async (subdeck: SubdeckData) => {
    try {
      const token = localStorage.getItem('accessToken') || '';
      const r = await fetch(
        `/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeck._id}/toggle-free`,
        { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const result = await r.json();
        setSubdecksState(prev =>
          prev.map(s => s._id === subdeck._id ? { ...s, isFree: result.isFree } : s)
        );
        setToast({
          message: result.isFree ? 'Deck liberado gratuitamente para todos!' : 'Gratuidade removida',
          type: 'info',
        });
      }
    } catch {
      setToast({ message: 'Erro ao alterar gratuidade', type: 'error' });
    }
  };

  // ── "Restringir temporariamente" (independente de Interditar) ──
  const handleConfirmRestrictSubdeck = async (message: string) => {
    if (!restrictingSubdeck) return;
    setRestrictSaving(true);
    try {
      const token = localStorage.getItem('accessToken') || '';
      const r = await fetch(
        `/api/ryan/admin/spaces/${spaceId}/subdecks/${restrictingSubdeck._id}/restrict`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ restricted: true, message }),
        }
      );
      if (r.ok) {
        const result = await r.json();
        setSubdecksState(prev =>
          prev.map(s => s._id === restrictingSubdeck._id
            ? { ...s, restricted: result.restricted, restrictedMessage: result.restrictedMessage }
            : s)
        );
        setToast({ message: 'Conteúdo restringido temporariamente!', type: 'success' });
        setRestrictingSubdeck(null);
      } else {
        const e = await r.json();
        setToast({ message: e.message || 'Erro ao restringir', type: 'error' });
      }
    } catch {
      setToast({ message: 'Erro de conexão', type: 'error' });
    } finally {
      setRestrictSaving(false);
    }
  };

  const handleRemoveRestrictionSubdeck = async (subdeck: SubdeckData) => {
    try {
      const token = localStorage.getItem('accessToken') || '';
      const r = await fetch(
        `/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeck._id}/restrict`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ restricted: false }),
        }
      );
      if (r.ok) {
        setSubdecksState(prev =>
          prev.map(s => s._id === subdeck._id ? { ...s, restricted: false } : s)
        );
        setToast({ message: 'Restrição removida — conteúdo normal para todos', type: 'info' });
      }
    } catch {
      setToast({ message: 'Erro ao remover restrição', type: 'error' });
    }
  };

  const handleRestrictedClick = (subdeck: SubdeckData) => {
    setRestrictedClickInfo({ name: subdeck.name, message: subdeck.restrictedMessage || '' });
  };

  // ── Edit subdeck ──
  const handleEditSubdeck = async () => {
    if (!editingSubdeck || !editForm.name.trim()) { setEditError('Nome obrigatório'); return; }
    setEditSaving(true);
    setEditError('');
    const token = localStorage.getItem('accessToken') || '';
    try {
      const r = await fetch(`/api/decks/${editingSubdeck.deckId}/subdecks/${editingSubdeck._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: editForm.name.trim(),
          description: editForm.description,
          colorId: editForm.colorId,
          iconId: editForm.iconId,
          badge: editForm.badge.trim() || null,
        }),
      });
      if (!r.ok) {
        const e = await r.json();
        setEditError(e.message || 'Erro ao salvar');
        return;
      }
      setEditingSubdeck(null);
      setToast({ message: 'Deck atualizado!', type: 'success' });
      loadField();
    } catch {
      setEditError('Erro de conexão');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Import apostila ──
  const handleImportApostila = async (importedDataList: any[]) => {
    const deckId = data?.space.deckId || subdecksState[0]?.deckId;
    if (!deckId) {
      setToast({ message: 'Deck interno não encontrado', type: 'error' });
      return;
    }
    const token = localStorage.getItem('accessToken') || '';
    let successCount = 0;
    const failed: string[] = [];

    for (const importedData of importedDataList) {
      const name = importedData.title || importedData.__subdeckName || 'Nova Apostila';
      try {
        const r = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name, description: '', type: 'apostila', tabId: SEM_ABA_TAB_SANITIZE(activeTabId) }),
        });
        if (!r.ok) {
          const e = await r.json();
          throw new Error(e.message || 'Erro ao criar apostila');
        }
        const created = await r.json();
        const newSubdeckId = created.subdeck._id;

        await resumoService.saveResumo(deckId, newSubdeckId, {
          title: importedData.title || name,
          blocks: importedData.blocks || [],
          pages: importedData.pages || [],
          quizzes: importedData.quizzes || {},
          metadata: {
            subdeckId: newSubdeckId,
            subdeckName: name,
            deckId,
            deckName: data?.space.name || '',
            lastEdited: new Date().toISOString(),
            version: 1,
          },
        } as any, false);
        successCount++;
      } catch (e: any) {
        failed.push(name);
      }
    }

    setShowImportApostilaModal(false);
    if (failed.length === 0) {
      setToast({
        message: successCount > 1 ? `${successCount} apostilas importadas!` : 'Apostila importada!',
        type: 'success'
      });
    } else {
      setToast({
        message: `${successCount} importada(s), ${failed.length} falhou(aram): ${failed.join(', ')}`,
        type: successCount > 0 ? 'info' : 'error'
      });
    }
    loadField();
  };

  // ── Delete subdeck ──
  const handleDeleteSubdeck = async (subdeckId: string, deckId: string) => {
    const token = localStorage.getItem('accessToken') || '';
    try {
      const r1 = await fetch(`/api/decks/${deckId}/subdecks/${subdeckId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r1.ok) {
        const e = await r1.json();
        setToast({ message: e.message || 'Erro ao excluir deck', type: 'error' });
        return;
      }
      await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeckId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setToast({ message: 'Deck excluído!', type: 'success' });
      loadField();
    } catch {
      setToast({ message: 'Erro ao excluir', type: 'error' });
    }
  };

  const hasTabs = (data?.tabs?.length ?? 0) > 0;

  const visibleSubdecks = !hasTabs
    ? subdecksState
    : activeTabId === SEM_ABA_TAB_ID
      ? subdecksState.filter(s => !s.tabId)
      : subdecksState.filter(s => s.tabId === activeTabId);

  // Busca do header (RyanFieldHero) — filtra decks (flashcards) e apostilas por nome,
  // aplicada sobre o recorte da aba atual. As contagens do header usam o total sem
  // filtro, pra não "piscar" números enquanto o admin digita.
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredSubdecks = normalizedSearch
    ? visibleSubdecks.filter(s => s.name.toLowerCase().includes(normalizedSearch))
    : visibleSubdecks;

  const activeSubdeck = activeSubdeckId ? subdecksState.find(s => s._id === activeSubdeckId) || null : null;

  const decksCount = visibleSubdecks.filter(s => s.type !== 'apostila').length;
  const apostilasCount = visibleSubdecks.filter(s => s.type === 'apostila').length;
  // Subdecks marcados "não contar no header" (revisão com cards reaproveitados
  // de outros subdecks) ficam fora da soma — não são cards novos.
  const flashcardsTotal = visibleSubdecks
    .filter(s => s.type !== 'apostila' && !s.excludeFromCount)
    .reduce((sum, s) => sum + (s.cardCount || 0), 0);

  // ── Hero de progresso (Continuar de onde parou / Top domínio) — sempre
  // recalculado a partir de visibleSubdecks (recorte da aba atual, sem o
  // filtro de busca), então acompanha a troca de aba automaticamente.
  const flashcardVisible = visibleSubdecks.filter(s => s.type !== 'apostila');
  const lastStudiedSubdeck = flashcardVisible
    .filter(s => s.lastReviewedAt)
    .sort((a, b) => new Date(b.lastReviewedAt!).getTime() - new Date(a.lastReviewedAt!).getTime())[0] || null;
  const rankedDomains = [...flashcardVisible].sort(
    (a, b) => (b.masteryPercentage ?? -1) - (a.masteryPercentage ?? -1)
  );
  const topDomains = rankedDomains.filter(s => (s.masteryPercentage ?? 0) > 0).slice(0, 3);

  // Quando a aba não tem nenhum deck de flashcards (só apostilas — bem comum),
  // o hero vira "modo apostila": mesma ideia, mas com base no progresso real
  // de leitura por página em vez de domínio de flashcards.
  const apostilaVisible = visibleSubdecks.filter(s => s.type === 'apostila');
  const lastReadApostila = apostilaVisible
    .filter(s => s.lastReadAt)
    .sort((a, b) => new Date(b.lastReadAt!).getTime() - new Date(a.lastReadAt!).getTime())[0] || null;
  const rankedApostilas = [...apostilaVisible].sort(
    (a, b) => (b.readPercentage ?? -1) - (a.readPercentage ?? -1)
  );
  const topApostilas = rankedApostilas.filter(s => (s.readPercentage ?? 0) > 0).slice(0, 3);

  const progressHeroMode: 'flashcards' | 'apostila' = flashcardVisible.length > 0 ? 'flashcards' : 'apostila';

  const switchTab = (id: string) => {
    if (id !== SEM_ABA_TAB_ID && !isAdmin) {
      const tab = data?.tabs.find(t => t._id === id);
      if (tab?.isBlocked) {
        setInterdictedName(tab.name);
        setShowInterdictedModal(true);
        return;
      }
    }
    setActiveTabId(id);
    setTabKey(k => k + 1);
  };

  // ── Render: modo grupos ──
  const renderGroupedSubdecks = () => {
    const currentTabId = activeTabId === SEM_ABA_TAB_ID || !activeTabId ? null : activeTabId;

    const tabGroups = groups
      .filter(g => {
        const gTabId = g.tabId || null;
        return gTabId === currentTabId;
      })
      .sort((a, b) => a.order - b.order);

    const ungroupedSubdecks = filteredSubdecks.filter(s => !s.groupId);

    return (
      <div>
        {tabGroups.map(group => {
          const groupSubdecks = filteredSubdecks.filter(s => s.groupId === group._id);
          return (
            <RyanSubdeckGroupContainer
              key={group._id}
              group={group}
              subdecks={groupSubdecks}
              isExpanded={openGroups.has(group._id)}
              onToggle={() => toggleGroup(group._id)}
              isAdmin={!!isAdmin}
              spaceId={spaceId!}
              spaceName={data?.space.name || ''}
              isVip={isVip}
              isEditor={isEditor}
              comprado={comprado}
              ryanConfig={ryanConfig}
              tabs={data?.tabs || []}
              onEditGroup={() => { setEditingGroup(group); setShowCreateGroupModal(true); }}
              onDeleteGroup={async () => {
                if (!window.confirm(`Excluir grupo "${group.name}"? Os decks ficarão sem grupo.`)) return;
                const token = localStorage.getItem('accessToken');
                const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/groups/${group._id}`, {
                  method: 'DELETE',
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                  setToast({ message: 'Grupo excluído!', type: 'success' });
                  loadField();
                  loadGroups();
                }
              }}
              onMoveGroup={newTabId => handleMoveGroup(group, newTabId)}
              onStudyGroup={() => handleStudyGroup(group, groupSubdecks)}
              onReorderSubdecks={reordered => handleGroupSubdecksReorder(group._id, reordered)}
              onDelete={handleDeleteSubdeck}
              onExport={handleExport}
              onExportApostila={handleExportApostila}
              onMove={sd => { setSubdeckToMove(sd); setShowMoveModal(true); }}
              onCopy={sd => { setSubdeckToCopy(sd); setShowCopyModal(true); }}
              onMarkRead={handleMarkRead}
              onToggleBlock={handleToggleBlock}
              onToggleHidden={handleToggleHidden}
              onToggleExcludeCount={handleToggleExcludeCount}
              onToggleFree={handleToggleFree}
              onOpenRestrict={sd => setRestrictingSubdeck(sd)}
              onRemoveRestriction={handleRemoveRestrictionSubdeck}
              onEdit={sd => { setEditingSubdeck(sd); setEditForm({ name: sd.name, description: sd.description || '', colorId: sd.colorId || 'floresta', iconId: sd.iconId || null, badge: sd.badge || '' }); setEditError(''); }}
              onBlockedClick={name => { setInterdictedName(name); setShowInterdictedModal(true); }}
              onRestrictedClick={handleRestrictedClick}
              onToast={(msg, type) => setToast({ message: msg, type })}
              onAssignGroup={sd => { setSubdeckForGroup(sd); setShowAssignGroupModal(true); }}
              onAssignCategory={sd => { setSubdeckForCategory(sd); setShowAssignCategoryModal(true); }}
              onConfigurePreview={handleConfigurePreview}
              onConfigureApostilaPreview={handleConfigureApostilaPreview}
              selectionMode={selectionMode}
              selectedSubdeckIds={selectedSubdeckIds}
              onToggleSelect={toggleSubdeckSelection}
            />
          );
        })}

        {/* Container "Sem grupo" — só mostra quando há subdecks órfãos */}
        {ungroupedSubdecks.length > 0 && (
          <RyanSubdeckGroupContainer
            key="__ungrouped__"
            group={null}
            subdecks={ungroupedSubdecks}
            isExpanded={openGroups.has('__ungrouped__')}
            onToggle={() => toggleGroup('__ungrouped__')}
            isAdmin={!!isAdmin}
            spaceId={spaceId!}
            spaceName={data?.space.name || ''}
            isVip={isVip}
            isEditor={isEditor}
            comprado={comprado}
            ryanConfig={ryanConfig}
            onReorderSubdecks={reordered => handleGroupSubdecksReorder(null, reordered)}
            onDelete={handleDeleteSubdeck}
            onExport={handleExport}
            onExportApostila={handleExportApostila}
            onMove={sd => { setSubdeckToMove(sd); setShowMoveModal(true); }}
            onCopy={sd => { setSubdeckToCopy(sd); setShowCopyModal(true); }}
            onMarkRead={handleMarkRead}
            onToggleBlock={handleToggleBlock}
            onToggleHidden={handleToggleHidden}
            onToggleExcludeCount={handleToggleExcludeCount}
            onToggleFree={handleToggleFree}
            onOpenRestrict={sd => setRestrictingSubdeck(sd)}
            onRemoveRestriction={handleRemoveRestrictionSubdeck}
            onEdit={sd => { setEditingSubdeck(sd); setEditForm({ name: sd.name, description: sd.description || '', colorId: sd.colorId || 'floresta', iconId: sd.iconId || null, badge: sd.badge || '' }); setEditError(''); }}
            onBlockedClick={name => { setInterdictedName(name); setShowInterdictedModal(true); }}
            onRestrictedClick={handleRestrictedClick}
            onToast={(msg, type) => setToast({ message: msg, type })}
            onAssignGroup={sd => { setSubdeckForGroup(sd); setShowAssignGroupModal(true); }}
            onAssignCategory={sd => { setSubdeckForCategory(sd); setShowAssignCategoryModal(true); }}
            onConfigurePreview={handleConfigurePreview}
            onConfigureApostilaPreview={handleConfigureApostilaPreview}
            selectionMode={selectionMode}
            selectedSubdeckIds={selectedSubdeckIds}
            onToggleSelect={toggleSubdeckSelection}
          />
        )}
      </div>
    );
  };

  const sharedCardProps = {
    spaceId: spaceId!,
    spaceName: data?.space.name || '',
    isAdmin: !!isAdmin,
    isVip,
    isEditor,
    comprado,
    ryanConfig,
    onDelete: handleDeleteSubdeck,
    onExport: handleExport,
    onExportApostila: handleExportApostila,
    onMove: (sd: SubdeckData) => { setSubdeckToMove(sd); setShowMoveModal(true); },
    onCopy: (sd: SubdeckData) => { setSubdeckToCopy(sd); setShowCopyModal(true); },
    onMarkRead: handleMarkRead,
    onToggleBlock: handleToggleBlock,
    onToggleHidden: handleToggleHidden,
    onToggleExcludeCount: handleToggleExcludeCount,
    onToggleFree: handleToggleFree,
    onOpenRestrict: (sd: SubdeckData) => setRestrictingSubdeck(sd),
    onRemoveRestriction: handleRemoveRestrictionSubdeck,
    onEdit: (sd: SubdeckData) => { setEditingSubdeck(sd); setEditForm({ name: sd.name, description: sd.description || '', colorId: sd.colorId || 'floresta', iconId: sd.iconId || null, badge: sd.badge || '' }); setEditError(''); },
    onBlockedClick: (name: string) => { setInterdictedName(name); setShowInterdictedModal(true); },
    onRestrictedClick: handleRestrictedClick,
    onToast: (msg: string, type: 'success' | 'error' | 'info') => setToast({ message: msg, type }),
    onAssignGroup: (sd: SubdeckData) => { setSubdeckForGroup(sd); setShowAssignGroupModal(true); },
    onAssignCategory: (sd: SubdeckData) => { setSubdeckForCategory(sd); setShowAssignCategoryModal(true); },
    onConfigurePreview: handleConfigurePreview,
    onConfigureApostilaPreview: handleConfigureApostilaPreview,
    selectionMode,
    selectedSubdeckIds,
    onToggleSelect: toggleSubdeckSelection,
  };

  return (
    <div className="ryan-field-page">
      {!loading && data && (
        <RyanFieldHero
          name={data.space.name}
          colorId={data.space.colorId}
          theme={data.space.theme}
          icon={data.space.icon}
          decksCount={decksCount}
          flashcardsTotal={flashcardsTotal}
          apostilasCount={apostilasCount}
          onBack={() => navigate('/ryan')}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isAdmin={!!isAdmin}
          adminMenuContent={
            <>
              <button
                className="ryan-field-admin-item"
                onClick={openSubdeckModal}
              >
                <Plus size={14} />
                Adicionar deck
              </button>
              <button
                className="ryan-field-admin-item"
                onClick={() => setShowImportModal(true)}
              >
                <Upload size={14} />
                Importar cards
              </button>
              <button
                className="ryan-field-admin-item"
                onClick={() => setShowImportApostilaModal(true)}
              >
                <Upload size={14} />
                Importar apostila
              </button>
              <button
                className="ryan-field-admin-item"
                onClick={openTabModal}
              >
                <Settings size={14} />
                Gerenciar abas
              </button>
              <button
                className="ryan-field-admin-item"
                onClick={openLayoutModal}
              >
                <LayoutGrid size={14} />
                Layout da página
              </button>
              <button
                className="ryan-field-admin-item"
                onClick={() => (selectionMode ? exitSelectionMode() : enterSelectionMode())}
              >
                <CheckSquare size={14} />
                {selectionMode ? 'Cancelar seleção' : 'Selecionar decks'}
              </button>
              {layoutMode === 'groups' && (
                <>
                  <button
                    className="ryan-field-admin-item"
                    onClick={() => { setEditingGroup(null); setShowCreateGroupModal(true); }}
                  >
                    <FolderPlus size={14} />
                    Criar grupo
                  </button>
                  <button
                    className="ryan-field-admin-item"
                    onClick={() => setShowManageGroupsModal(true)}
                  >
                    <Layers size={14} />
                    Gerenciar grupos
                  </button>
                </>
              )}
            </>
          }
        />
      )}

      {!loading && data && (
        <div
          className="ryan-field-progress-section"
          style={{ '--field-color': getRyanFieldColor(data.space.colorId) } as React.CSSProperties}
        >
          {hasTabs && (
            <div className="ryan-field-tabs">
              {data.tabs
                .slice()
                .sort((a, b) => a.order - b.order)
                .map(tab => (
                  <button
                    key={tab._id}
                    className={`ryan-field-tab ${activeTabId === tab._id ? 'ryan-field-tab--active' : ''}${tab.isBlocked ? ' ryan-field-tab--blocked' : ''}`}
                    onClick={() => switchTab(tab._id)}
                  >
                    {tab.isBlocked && <Lock size={11} />}
                    {tab.name}
                  </button>
                ))}
              {isAdmin && (
                <button
                  className={`ryan-field-tab ryan-field-tab--no-tab ${activeTabId === SEM_ABA_TAB_ID ? 'ryan-field-tab--active' : ''}`}
                  onClick={() => switchTab(SEM_ABA_TAB_ID)}
                >
                  Sem aba
                </button>
              )}
            </div>
          )}

          <RyanFieldProgressHero
            colorId={data.space.colorId}
            mode={progressHeroMode}
            lastStudied={
              progressHeroMode === 'flashcards'
                ? lastStudiedSubdeck
                : (lastReadApostila && {
                    ...lastReadApostila,
                    masteryPercentage: lastReadApostila.readPercentage ?? 0,
                    lastReviewedAt: lastReadApostila.lastReadAt,
                  }) || null
            }
            topDomains={
              progressHeroMode === 'flashcards'
                ? topDomains
                : topApostilas.map(s => ({ ...s, masteryPercentage: s.readPercentage ?? 0, lastReviewedAt: s.lastReadAt }))
            }
            onContinue={progressHeroMode === 'flashcards' ? handleContinueStudy : handleContinueApostila}
            onOpenRanking={() => setShowRankingModal(true)}
          />
        </div>
      )}

      <RyanDomainRankingModal
        isOpen={showRankingModal}
        onClose={() => setShowRankingModal(false)}
        colorId={data?.space.colorId || 'floresta'}
        mode={progressHeroMode}
        ranked={
          progressHeroMode === 'flashcards'
            ? rankedDomains
            : rankedApostilas.map(s => ({ ...s, masteryPercentage: s.readPercentage ?? 0, lastReviewedAt: s.lastReadAt }))
        }
      />

      {selectionMode && selectedSubdeckIds.size > 0 && (
        <div className="ryan-bulk-bar">
          <span className="ryan-bulk-bar-count">
            {selectedSubdeckIds.size} deck{selectedSubdeckIds.size !== 1 ? 's' : ''} selecionado{selectedSubdeckIds.size !== 1 ? 's' : ''}
          </span>
          <div className="ryan-bulk-bar-actions">
            <button className="ryan-bulk-bar-btn-move" onClick={() => setShowMoveTabModal(true)}>
              <ArrowRightLeft size={14} />
              Mover para aba
            </button>
            <button className="ryan-bulk-bar-btn-cancel" onClick={exitSelectionMode}>
              <X size={14} />
              Cancelar seleção
            </button>
          </div>
        </div>
      )}

      <div className="ryan-field-content">
        {loading && (
          <div className="ryan-field-spinner-wrap">
            <div className="ryan-field-spinner" />
          </div>
        )}

        {!loading && error && (
          <div className="ryan-field-empty">
            <p style={{ color: '#dc2626' }}>{error}</p>
          </div>
        )}

        {!loading && !error && filteredSubdecks.length === 0 && (
          <div className="ryan-field-empty">
            <BookOpen size={48} color="#0D3B0E" style={{ opacity: 0.35 }} />
            <p>{normalizedSearch ? 'Nenhum resultado para essa busca.' : 'Nenhum deck aqui ainda.'}</p>
          </div>
        )}

        {!loading && !error && filteredSubdecks.length > 0 && (
          <div key={tabKey}>
            {layoutMode === 'groups' ? (
              renderGroupedSubdecks()
            ) : layoutMode === 'byType' ? (
              (() => {
                // Fonte de verdade é RyanDisplayCategory.order — typeSectionOrder só serve
                // de base pra semear a ordem inicial das 2 categorias padrão (ver backend).
                // Fallback defensivo caso as categorias ainda não tenham sido carregadas.
                const sections = displayCategories.length > 0
                  ? [...displayCategories]
                      .sort((a, b) => a.order - b.order)
                      .map(category => ({
                        id: category._id,
                        name: category.name,
                        color: category.color,
                        gridColumns: category.gridColumns ?? null,
                        subdecks: filteredSubdecks.filter(s => resolveCategoryId(s) === category._id),
                      }))
                  : typeSectionOrder.map(type => ({
                      id: type,
                      name: type === 'apostila' ? 'Apostilas' : 'Flashcards',
                      color: type === 'apostila' ? apostilasColor : flashcardsColor,
                      gridColumns: null,
                      subdecks: filteredSubdecks.filter(s => resolveCategoryId(s) === type),
                    }));

                const visibleSections = sections.filter(section => section.subdecks.length > 0);

                return (
                  <>
                    {visibleSections.map((section, idx) => (
                      <div key={section.id} style={{ marginBottom: idx < visibleSections.length - 1 ? 32 : 0 }}>
                        <RyanTypeSeparator name={section.name} color={section.color} />
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragStart={isAdmin ? handleSubdeckDragStart : undefined}
                          onDragEnd={isAdmin ? e => { setActiveSubdeckId(null); handleTypeSectionDragEnd(e, section.id); } : undefined}
                        >
                          <SortableContext items={section.subdecks.map(s => s._id)} strategy={rectSortingStrategy}>
                            <div className={`ryan-subdecks-grid${section.gridColumns === 2 ? ' ryan-subdecks-grid--cols-2' : ''}`}>
                              {section.subdecks.map(subdeck => (
                                <SortableSubdeck
                                  key={subdeck._id}
                                  subdeck={subdeck}
                                  accentColor={section.color}
                                  {...sharedCardProps}
                                />
                              ))}
                            </div>
                          </SortableContext>
                          <DragOverlay dropAnimation={{ duration: 220, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' }}>
                            {activeSubdeck ? (
                              <SubdeckDragGhost subdeck={activeSubdeck} accentColor={section.color} />
                            ) : null}
                          </DragOverlay>
                        </DndContext>
                      </div>
                    ))}
                  </>
                );
              })()
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={isAdmin ? handleSubdeckDragStart : undefined}
                onDragEnd={isAdmin ? handleSubdeckDragEnd : undefined}
              >
                <SortableContext
                  items={filteredSubdecks.map(s => s._id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="ryan-subdecks-grid">
                    {filteredSubdecks.map(subdeck => (
                      <SortableSubdeck
                        key={subdeck._id}
                        subdeck={subdeck}
                        accentColor={getRyanSubdeckColor(subdeck.colorId)}
                        {...sharedCardProps}
                      />
                    ))}
                  </div>
                </SortableContext>
                <DragOverlay dropAnimation={{ duration: 220, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' }}>
                  {activeSubdeck ? (
                    <SubdeckDragGhost subdeck={activeSubdeck} accentColor={getRyanSubdeckColor(activeSubdeck.colorId)} />
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        )}
      </div>

      {/* ── Modal: Layout ── */}
      {showLayoutModal && (
        <div className="ryan-modal-overlay" onClick={() => setShowLayoutModal(false)}>
          <div className="ryan-modal" onClick={e => e.stopPropagation()}>
            <div className="ryan-modal-header">
              <h3 className="ryan-modal-title">Layout da Página</h3>
              <button className="ryan-modal-close-btn" onClick={() => setShowLayoutModal(false)}><X size={18} /></button>
            </div>
            <div className="ryan-modal-body">
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginBottom: 16 }}>
                Escolha como os decks são exibidos neste campo.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                    borderRadius: 12, border: `2px solid ${layoutMode === 'grid' ? '#0D3B0E' : 'rgba(0,0,0,0.1)'}`,
                    background: layoutMode === 'grid' ? 'rgba(13,59,14,0.05)' : 'white',
                    cursor: 'pointer', textAlign: 'left', transition: 'all 150ms',
                  }}
                  onClick={() => handleSetLayoutMode('grid')}
                  disabled={layoutSaving}
                >
                  <LayoutGrid size={24} color="#0D3B0E" />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0D3B0E', fontFamily: 'Inter, sans-serif' }}>Grid</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>
                      Todos os decks em grid, sem grupos.
                    </div>
                  </div>
                  {layoutMode === 'grid' && <span style={{ marginLeft: 'auto', fontSize: 12, color: '#0D3B0E', fontWeight: 600 }}>Atual</span>}
                </button>
                <button
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                    borderRadius: 12, border: `2px solid ${layoutMode === 'groups' ? '#0D3B0E' : 'rgba(0,0,0,0.1)'}`,
                    background: layoutMode === 'groups' ? 'rgba(13,59,14,0.05)' : 'white',
                    cursor: 'pointer', textAlign: 'left', transition: 'all 150ms',
                  }}
                  onClick={() => handleSetLayoutMode('groups')}
                  disabled={layoutSaving}
                >
                  <Layers size={24} color="#0D3B0E" />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0D3B0E', fontFamily: 'Inter, sans-serif' }}>Grupos</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>
                      Decks organizados em containers colapsáveis por grupo.
                    </div>
                  </div>
                  {layoutMode === 'groups' && <span style={{ marginLeft: 'auto', fontSize: 12, color: '#0D3B0E', fontWeight: 600 }}>Atual</span>}
                </button>
                <button
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                    borderRadius: 12, border: `2px solid ${layoutMode === 'byType' ? '#0D3B0E' : 'rgba(0,0,0,0.1)'}`,
                    background: layoutMode === 'byType' ? 'rgba(13,59,14,0.05)' : 'white',
                    cursor: 'pointer', textAlign: 'left', transition: 'all 150ms',
                  }}
                  onClick={() => layoutMode === 'byType' ? setShowManageCategoriesModal(true) : handleSetLayoutMode('byType')}
                  disabled={layoutSaving}
                >
                  <Palette size={24} color="#0D3B0E" />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0D3B0E', fontFamily: 'Inter, sans-serif' }}>Por Tipo</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>
                      Separa os decks em seções por categoria, cada uma com sua cor.
                    </div>
                  </div>
                  {layoutMode === 'byType' && (
                    <span style={{
                      marginLeft: 'auto', fontSize: 12, color: '#0D3B0E', fontWeight: 700,
                      padding: '6px 12px', borderRadius: 8, background: 'rgba(13,59,14,0.1)',
                    }}>
                      Gerenciar
                    </span>
                  )}
                </button>
              </div>

              <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                <label className="ryan-modal-label" style={{ display: 'block', marginBottom: 4 }}>
                  Cor padrão dos subdecks novos
                </label>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginBottom: 10 }}>
                  Cor com que cada subdeck novo nasce (criar, importar arquivo ou importar apostila) — evita ter que trocar a cor manualmente toda vez. Editar a cor de um subdeck direto sempre continua valendo por cima disso.
                </p>
                <div className="ryan-subdeck-color-groups">
                  {getOrderedFieldColorFamilies(data?.space.colorId).map(family => {
                    const isFieldColor = family.id === (data?.space.colorId || 'floresta');
                    return (
                      <div
                        key={family.id}
                        className={`ryan-subdeck-color-group${isFieldColor ? ' ryan-subdeck-color-group--fieldmatch' : ''}`}
                        style={isFieldColor ? ({ '--fm-color': family.color } as React.CSSProperties) : undefined}
                      >
                        <span className="ryan-subdeck-color-group-label">
                          {family.label}
                          {isFieldColor && <span className="ryan-subdeck-color-group-badge">cor deste campo</span>}
                        </span>
                        <div className="ryan-subdeck-color-group-swatches">
                          {RYAN_SUBDECK_COLORS.filter(c => c.familyId === family.id).map(c => (
                            <button
                              key={c.id}
                              type="button"
                              className={`ryan-color-dot-btn${defaultSubdeckColorId === c.id ? ' selected' : ''}${c.isParent ? ' ryan-color-dot-btn--parent' : ''}`}
                              style={{ background: c.color }}
                              onClick={() => handleSetDefaultSubdeckColor(c.id)}
                              disabled={layoutSaving}
                              title={c.isParent ? `${family.label} — cor do campo` : `${family.label} — ${c.tone}`}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                <label className="ryan-modal-label" style={{ display: 'block', marginBottom: 4 }}>
                  Link de compra
                </label>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginBottom: 10 }}>
                  Para onde vai o botão "Quero adquirir" quando alguém sem o produto clica num deck deste campo (abre em nova aba). Normalmente a página pública do produto, ex.: /ryan/produto/meu-produto. Vazio = mostra o produto encontrado automaticamente.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="ryan-modal-input"
                    placeholder="https://... ou /ryan/produto/..."
                    value={purchaseUrlDraft}
                    onChange={e => setPurchaseUrlDraft(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSavePurchaseUrl()}
                  />
                  <button
                    className="ryan-modal-save-btn"
                    onClick={handleSavePurchaseUrl}
                    disabled={purchaseUrlSaving || purchaseUrlDraft.trim() === (data?.space.purchaseUrl || '')}
                  >
                    {purchaseUrlSaving ? 'Salvando…' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
            <div className="ryan-modal-footer">
              <button className="ryan-modal-cancel-btn" onClick={() => setShowLayoutModal(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Mover subdecks selecionados para aba (bulk) ── */}
      {showMoveTabModal && (
        <div className="ryan-modal-overlay" onClick={() => !movingTab && setShowMoveTabModal(false)}>
          <div className="ryan-modal" onClick={e => e.stopPropagation()}>
            <div className="ryan-modal-header">
              <h3 className="ryan-modal-title">Mover para Aba</h3>
              <button className="ryan-modal-close-btn" onClick={() => setShowMoveTabModal(false)} disabled={movingTab}>
                <X size={18} />
              </button>
            </div>
            <div className="ryan-modal-body">
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginBottom: 14 }}>
                {selectedSubdeckIds.size} deck{selectedSubdeckIds.size !== 1 ? 's' : ''} selecionado{selectedSubdeckIds.size !== 1 ? 's' : ''}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  style={{
                    display: 'flex', alignItems: 'center', padding: '10px 14px',
                    borderRadius: 10, border: '2px solid rgba(0,0,0,0.08)', background: '#f8faf8',
                    cursor: movingTab ? 'not-allowed' : 'pointer', textAlign: 'left', transition: 'all 150ms',
                    fontSize: 14, fontWeight: 600, color: '#111', fontFamily: 'Inter, sans-serif',
                  }}
                  onClick={() => handleBulkMoveToTab(null)}
                  disabled={movingTab}
                >
                  Sem aba
                </button>
                {(data?.tabs || []).slice().sort((a, b) => a.order - b.order).map(tab => (
                  <button
                    key={tab._id}
                    style={{
                      display: 'flex', alignItems: 'center', padding: '10px 14px',
                      borderRadius: 10, border: '2px solid rgba(0,0,0,0.08)', background: '#f8faf8',
                      cursor: movingTab ? 'not-allowed' : 'pointer', textAlign: 'left', transition: 'all 150ms',
                      fontSize: 14, fontWeight: 600, color: '#111', fontFamily: 'Inter, sans-serif',
                    }}
                    onClick={() => handleBulkMoveToTab(tab._id)}
                    disabled={movingTab}
                  >
                    {tab.name}
                  </button>
                ))}
                {(data?.tabs?.length ?? 0) === 0 && (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: '16px 0', fontFamily: 'Inter, sans-serif' }}>
                    Nenhuma aba criada neste campo ainda.
                  </p>
                )}
              </div>
            </div>
            <div className="ryan-modal-footer">
              <button className="ryan-modal-cancel-btn" onClick={() => setShowMoveTabModal(false)} disabled={movingTab}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Gerenciar Abas ── */}
      {showTabModal && (
        <div className="ryan-modal-overlay" onClick={() => setShowTabModal(false)}>
          <div className="ryan-modal" onClick={e => e.stopPropagation()}>
            <div className="ryan-modal-header">
              <h3 className="ryan-modal-title">Gerenciar Abas</h3>
              <button className="ryan-modal-close-btn" onClick={() => setShowTabModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="ryan-modal-body">
              {sortedTabs.length === 0 && (
                <p className="ryan-tab-modal-empty">Nenhuma aba criada ainda.</p>
              )}

              {sortedTabs.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleTabDragEnd}
                >
                  <SortableContext
                    items={sortedTabs.map(t => t._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {sortedTabs.map(tab => (
                      <SortableTabRow
                        key={tab._id}
                        tab={tab}
                        renameValue={tabRenames[tab._id] ?? tab.name}
                        onChange={val => setTabRenames(r => ({ ...r, [tab._id]: val }))}
                        onRename={() => handleRenameTab(tab._id, tab.name)}
                        onDelete={() => handleDeleteTab(tab._id)}
                        onToggleBlock={() => handleToggleTabBlock(tab._id, !!tab.isBlocked)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}

              {(() => {
                const existingNames = new Set(sortedTabs.map(t => t.name.trim().toLowerCase()));
                const availablePresets = RYAN_TAB_NAME_PRESETS.filter(
                  name => !existingNames.has(name.toLowerCase())
                );
                if (availablePresets.length === 0) return null;
                return (
                  <div className="ryan-tab-mgmt-presets">
                    <span className="ryan-tab-mgmt-presets-label">Adicionar rápido</span>
                    <div className="ryan-tab-mgmt-presets-grid">
                      {availablePresets.map(name => (
                        <button
                          key={name}
                          type="button"
                          className="ryan-tab-preset-chip"
                          onClick={() => handleCreateTab(name)}
                          disabled={tabSaving}
                        >
                          {name} <Plus size={12} />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div className="ryan-tab-mgmt-add">
                <input
                  className="ryan-modal-input ryan-tab-mgmt-input"
                  value={newTabName}
                  onChange={e => setNewTabName(e.target.value)}
                  placeholder="Nome da nova aba…"
                  onKeyDown={e => e.key === 'Enter' && handleCreateTab()}
                />
                <button
                  className="ryan-modal-save-btn"
                  onClick={() => handleCreateTab()}
                  disabled={tabSaving || !newTabName.trim()}
                >
                  {tabSaving ? '…' : 'Criar'}
                </button>
              </div>
            </div>
            <div className="ryan-modal-footer">
              <button className="ryan-modal-cancel-btn" onClick={() => setShowTabModal(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Adicionar Subdeck ── */}
      {showSubdeckModal && (
        <div className="ryan-modal-overlay" onClick={() => setShowSubdeckModal(false)}>
          <div className="ryan-modal" onClick={e => e.stopPropagation()}>
            <div className="ryan-modal-header">
              <h3 className="ryan-modal-title">Adicionar Deck</h3>
              <button className="ryan-modal-close-btn" onClick={() => setShowSubdeckModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="ryan-modal-body">
              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Nome *</label>
                <input
                  className="ryan-modal-input"
                  value={subdeckForm.name}
                  onChange={e => setSubdeckForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ex: Exame Físico Geral"
                  autoFocus
                />
              </div>
              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Descrição</label>
                <textarea
                  className="ryan-modal-textarea"
                  value={subdeckForm.description}
                  onChange={e => setSubdeckForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Descrição breve…"
                />
              </div>
              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Tipo</label>
                <div className="ryan-modal-radio-group">
                  <label className="ryan-modal-radio-label">
                    <input
                      type="radio"
                      value="flashcards"
                      checked={subdeckForm.type === 'flashcards'}
                      onChange={() => setSubdeckForm(f => ({ ...f, type: 'flashcards' }))}
                    />
                    Flashcards
                  </label>
                  <label className="ryan-modal-radio-label">
                    <input
                      type="radio"
                      value="apostila"
                      checked={subdeckForm.type === 'apostila'}
                      onChange={() => setSubdeckForm(f => ({ ...f, type: 'apostila' }))}
                    />
                    Apostila
                  </label>
                </div>
              </div>
              {(data?.tabs?.length ?? 0) > 0 && (
                <div className="ryan-modal-field">
                  <label className="ryan-modal-label">Aba</label>
                  <select
                    className="ryan-modal-select"
                    value={subdeckForm.tabId}
                    onChange={e => setSubdeckForm(f => ({ ...f, tabId: e.target.value, groupId: '' }))}
                  >
                    <option value="">Sem aba</option>
                    {data!.tabs
                      .slice()
                      .sort((a, b) => a.order - b.order)
                      .map(tab => (
                        <option key={tab._id} value={tab._id}>{tab.name}</option>
                      ))}
                  </select>
                </div>
              )}
              {layoutMode === 'groups' && (
                <div className="ryan-modal-field">
                  <label className="ryan-modal-label">Grupo</label>
                  <select
                    className="ryan-modal-select"
                    value={subdeckForm.groupId}
                    onChange={e => setSubdeckForm(f => ({ ...f, groupId: e.target.value }))}
                  >
                    <option value="">Sem grupo</option>
                    {groups
                      .filter(g => (g.tabId || null) === (subdeckForm.tabId || null))
                      .sort((a, b) => a.order - b.order)
                      .map(g => (
                        <option key={g._id} value={g._id}>{g.name}</option>
                      ))}
                  </select>
                </div>
              )}
              {subdeckError && <div className="ryan-modal-error">{subdeckError}</div>}
            </div>
            <div className="ryan-modal-footer">
              <button className="ryan-modal-cancel-btn" onClick={() => setShowSubdeckModal(false)}>
                Cancelar
              </button>
              <button className="ryan-modal-save-btn" onClick={handleAddSubdeck} disabled={subdeckSaving}>
                {subdeckSaving ? 'Criando…' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Importar subdeck (flashcards) ── */}
      {showImportModal && layoutMode === 'groups' && groups.length > 0 && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10001, pointerEvents: 'none', display: 'flex', justifyContent: 'center', paddingTop: 72 }}>
          <div style={{ background: 'white', border: '1px solid rgba(13,59,14,0.15)', borderRadius: 12, padding: '12px 16px', pointerEvents: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 260 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#0D3B0E', fontFamily: 'Inter, sans-serif', marginBottom: 8 }}>Importar para grupo</div>
            <select
              className="ryan-modal-select"
              value={importGroupId}
              onChange={e => setImportGroupId(e.target.value)}
              style={{ fontSize: 13 }}
            >
              <option value="">Sem grupo</option>
              {groups
                .filter(g => (g.tabId || null) === SEM_ABA_TAB_SANITIZE(activeTabId))
                .sort((a, b) => a.order - b.order)
                .map(g => (
                  <option key={g._id} value={g._id}>{g.name}</option>
                ))}
            </select>
          </div>
        </div>
      )}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => { setShowImportModal(false); setImportFiles([]); setImportGroupId(''); }}
        onImport={handleImportSubdeck}
        onFilesSelect={setImportFiles}
        selectedFiles={importFiles}
        type="subdeck"
        importing={importing}
        acceptZip
        progressLabel={importProgress}
      />

      {/* ── Modal: Importar apostila ── */}
      {showImportApostilaModal && (
        <ImportApostilaModal
          onImport={handleImportApostila}
          onClose={() => setShowImportApostilaModal(false)}
        />
      )}

      {/* ── Modal: Mover subdeck ── */}
      {showMoveModal && subdeckToMove && (
        <RyanMoveModal
          spaceId={spaceId!}
          subdeckId={subdeckToMove._id}
          subdeckName={subdeckToMove.name}
          currentTabs={data?.tabs || []}
          onClose={() => { setShowMoveModal(false); setSubdeckToMove(null); }}
          onSuccess={loadField}
          onToast={(msg, type) => setToast({ message: msg, type })}
        />
      )}

      {/* ── Modal: Copiar subdeck ── */}
      {showCopyModal && subdeckToCopy && (
        <RyanCopyModal
          spaceId={spaceId!}
          subdeckId={subdeckToCopy._id}
          subdeckName={subdeckToCopy.name}
          currentTabs={data?.tabs || []}
          onClose={() => { setShowCopyModal(false); setSubdeckToCopy(null); }}
          onSuccess={loadField}
          onToast={(msg, type) => setToast({ message: msg, type })}
        />
      )}

      {/* ── Modal: Editar subdeck ── */}
      {editingSubdeck && (
        <div className="ryan-modal-overlay" onClick={() => setEditingSubdeck(null)}>
          <div className="ryan-modal" onClick={e => e.stopPropagation()}>
            <div className="ryan-modal-header">
              <h3 className="ryan-modal-title">Editar Deck</h3>
              <button className="ryan-modal-close-btn" onClick={() => setEditingSubdeck(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="ryan-modal-body">
              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Nome *</label>
                <input
                  className="ryan-modal-input"
                  value={editForm.name}
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleEditSubdeck()}
                />
              </div>
              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Descrição</label>
                <textarea
                  className="ryan-modal-textarea"
                  value={editForm.description}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Descrição breve…"
                />
              </div>
              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Badge (opcional)</label>
                <input
                  className="ryan-modal-input"
                  value={editForm.badge}
                  onChange={e => setEditForm(f => ({ ...f, badge: e.target.value }))}
                  placeholder='Ex: "Revisão"'
                  maxLength={20}
                />
                <span className="ryan-modal-hint">
                  Aparece como um selo pequeno no card, ao lado da contagem de págs./cards — sem aumentar o tamanho do bloco.
                </span>
              </div>
              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Cor do card</label>
                {/* Organizado por família (as mesmas 22 cores de campo), cada
                    uma com 5 tons — assim o card sempre conversa com a cor do
                    campo (que define o header/hero da página), em vez de uma
                    paleta solta e desconectada. O tom "medio" é a cor pai (a
                    cor pura do campo) e ganha um anel de destaque. Além disso,
                    só a LINHA da cor que este campo usa de verdade fica com
                    um destaque permanente (tipo um hover fixo) — não todas. */}
                <div className="ryan-subdeck-color-groups">
                  {getOrderedFieldColorFamilies(data?.space.colorId).map(family => {
                    const isFieldColor = family.id === (data?.space.colorId || 'floresta');
                    return (
                      <div
                        key={family.id}
                        className={`ryan-subdeck-color-group${isFieldColor ? ' ryan-subdeck-color-group--fieldmatch' : ''}`}
                        style={isFieldColor ? ({ '--fm-color': family.color } as React.CSSProperties) : undefined}
                      >
                        <span className="ryan-subdeck-color-group-label">
                          {family.label}
                          {isFieldColor && <span className="ryan-subdeck-color-group-badge">cor deste campo</span>}
                        </span>
                        <div className="ryan-subdeck-color-group-swatches">
                          {RYAN_SUBDECK_COLORS.filter(c => c.familyId === family.id).map(c => (
                            <button
                              key={c.id}
                              type="button"
                              className={`ryan-color-dot-btn${editForm.colorId === c.id ? ' selected' : ''}${c.isParent ? ' ryan-color-dot-btn--parent' : ''}`}
                              style={{ background: c.color }}
                              onClick={() => setEditForm(f => ({ ...f, colorId: c.id }))}
                              title={c.isParent ? `${family.label} — cor do campo` : `${family.label} — ${c.tone}`}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="ryan-modal-field">
                <label className="ryan-modal-label">Ícone</label>
                <RyanSubdeckIconPicker
                  value={editForm.iconId}
                  onChange={iconId => setEditForm(f => ({ ...f, iconId }))}
                />
              </div>
              {editError && <div className="ryan-modal-error">{editError}</div>}
            </div>
            <div className="ryan-modal-footer">
              <button className="ryan-modal-cancel-btn" onClick={() => setEditingSubdeck(null)}>
                Cancelar
              </button>
              <button className="ryan-modal-save-btn" onClick={handleEditSubdeck} disabled={editSaving}>
                {editSaving ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Criar / Editar grupo ── */}
      {showCreateGroupModal && (
        <RyanCreateGroupModal
          spaceId={spaceId!}
          tabs={data?.tabs || []}
          activeTabId={activeTabId === SEM_ABA_TAB_ID ? '' : activeTabId}
          editingGroup={editingGroup}
          onClose={() => { setShowCreateGroupModal(false); setEditingGroup(null); }}
          onSuccess={() => { loadGroups(); loadField(); }}
          onToast={(msg, type) => setToast({ message: msg, type })}
        />
      )}

      {/* ── Modal: Gerenciar grupos ── */}
      {showManageGroupsModal && (
        <RyanManageGroupsModal
          spaceId={spaceId!}
          groups={groups}
          tabs={data?.tabs || []}
          activeTabId={activeTabId === SEM_ABA_TAB_ID ? '' : activeTabId}
          onClose={() => setShowManageGroupsModal(false)}
          onEdit={group => { setEditingGroup(group); setShowCreateGroupModal(true); }}
          onDelete={async group => {
            const token = localStorage.getItem('accessToken');
            await fetch(`/api/ryan/admin/spaces/${spaceId}/groups/${group._id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token}` },
            });
            loadGroups();
            loadField();
          }}
          onSuccess={() => { loadGroups(); loadField(); }}
          onToast={(msg, type) => setToast({ message: msg, type })}
        />
      )}

      {/* ── Modal: Atribuir subdeck a grupo ── */}
      {showAssignGroupModal && subdeckForGroup && (
        <RyanAssignGroupModal
          spaceId={spaceId!}
          subdeck={subdeckForGroup}
          groups={groups}
          activeTabId={activeTabId}
          onClose={() => { setShowAssignGroupModal(false); setSubdeckForGroup(null); }}
          onSuccess={() => { loadField(); loadGroups(); }}
          onToast={(msg, type) => setToast({ message: msg, type })}
        />
      )}

      {/* ── Modal: Gerenciar categorias de exibição (modo "Por Tipo") ── */}
      {showManageCategoriesModal && (
        <RyanManageDisplayCategoriesModal
          spaceId={spaceId!}
          fieldColorId={data?.space.colorId}
          categories={displayCategories}
          onClose={() => {
            setShowManageCategoriesModal(false);
            // Exclusões desvinculam displayCategoryId de subdecks no backend — recarrega
            // pra não deixar subdecksState com referência a uma categoria que já era
            loadField();
          }}
          onChange={setDisplayCategories}
          onToast={(msg, type) => setToast({ message: msg, type })}
        />
      )}

      {/* ── Modal: Atribuir categoria de exibição a um subdeck ── */}
      {showAssignCategoryModal && subdeckForCategory && (
        <RyanAssignCategoryModal
          spaceId={spaceId!}
          subdeck={subdeckForCategory}
          categories={displayCategories}
          onClose={() => { setShowAssignCategoryModal(false); setSubdeckForCategory(null); }}
          onSuccess={loadField}
          onToast={(msg, type) => setToast({ message: msg, type })}
        />
      )}

      {/* ── Modal: Interditado ── */}
      <InterdictedModal
        isOpen={showInterdictedModal}
        onClose={() => setShowInterdictedModal(false)}
        entityType="subdeck"
        entityName={interdictedName}
      />

      {/* ── Modal: Restringir temporariamente (admin configura) ── */}
      {restrictingSubdeck && (
        <RyanRestrictModal
          entityName={restrictingSubdeck.name}
          initialMessage={restrictingSubdeck.restrictedMessage}
          saving={restrictSaving}
          onClose={() => setRestrictingSubdeck(null)}
          onConfirm={handleConfirmRestrictSubdeck}
        />
      )}

      {/* ── Modal: Conteúdo restrito (usuário comum tentou acessar) — função
          independente de "Interditado" acima, mensagem própria do admin ── */}
      <InterdictedModal
        isOpen={!!restrictedClickInfo}
        onClose={() => setRestrictedClickInfo(null)}
        entityType="subdeck"
        entityName={restrictedClickInfo?.name}
        variant="restricted"
        customMessage={restrictedClickInfo?.message}
      />

      {/* ── Modal: Configurar prévia de flashcards ── */}
      {showPreviewModal && subdeckForPreview && (
        <RyanFlashcardPreviewModal
          spaceId={spaceId!}
          subdeck={subdeckForPreview}
          onClose={() => { setShowPreviewModal(false); setSubdeckForPreview(null); }}
          onToast={(msg, type) => setToast({ message: msg, type })}
        />
      )}

      {/* ── Modal: Configurar prévia de apostila ── */}
      {showApostilaPreviewModal && subdeckForApostilaPreview && (
        <RyanApostilaPreviewModal
          spaceId={spaceId!}
          subdeck={subdeckForApostilaPreview}
          onClose={() => { setShowApostilaPreviewModal(false); setSubdeckForApostilaPreview(null); }}
          onToast={(msg, type) => setToast({ message: msg, type })}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

export default RyanFieldPage;
